import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export class GetEventEmployeeAbsenceListParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class GetEventEmployeesParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class GetEventStaffPositionEmployeesParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class GetEventCompetencyOptionsParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class GetEventAttributeOptionsParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  attribute_id: string;
}

export class GetVacationScheduleEventParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class GetAvailableScheduleEventFiltersParams {
  @IsUUID()
  @IsNotEmpty()
  schedule_id: string;
}

export class GetScheduleFavoritesEventsParams {
  @IsUUID()
  @IsNotEmpty()
  schedule_id: string;
}

export class GetScheduleEventsXlsxParams {
  @IsUUID()
  @IsNotEmpty()
  schedule_id: string;
}

export class GetScheduleEventsParams {
  @IsUUID()
  @IsNotEmpty()
  schedule_id: string;
}

export class EventAttributeCacheFileParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  attribute_id: string;
}

export class GetEventAttributeFileParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  attribute_id: string;
}

export class MarkDocumentAsReadParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  document_id: string;
}

export class GetEventDocumentFileHashParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  document_id: string;
}

export class EventFlowPdfParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class RequestEventDocumentDocxFileParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class GetEventDocumentZipParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  document_id: string;
}

export class GetEventDocumentFileWithStampParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  document_id: string;
}

export class GetEventDocumentFileParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  document_id: string;
}

export class DoCompetencyProfileParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class DoCompetencyEvalParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class DoPepSignParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class DoUkepSignParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class RequestUnepSignBatchCodeParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class DoUnepSignBatchParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class RequestUnepSignCodeParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class DoUnepSignParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class DocumentGenerateFromTemplateParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class DocumentGeneratePreviewFromTemplateParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class DoDeclineSignParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class DoDeclineParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export type ActionType = string;

export class DoRetryParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;

  @IsIn([
    'upload',
    'upload_batch',
    'accept',
    'ukep_sign',
    'ukep_sign_batch',
    'unep_sign',
    'unep_sign_batch',
    'pep_sign',
    'pep_sign_batch',
    'generate_document_from_template',
    'decline',
    'decline_sign',
    'return',
    'competency_eval',
    'competency_profile',
    'system',
    'system_validate_sign',
    'system_set_vacation_days',
    'system_sync_scheduled_vacations',
    'system_booking_hook',
    'system_booking_approve',
    'system_booking_limits_exceeded_approve',
    'system_booking_trip_create',
    'system_booking_trip_info',
    'system_booking_trip_change',
    'system_booking_trip_ordering',
    'system_booking_trip_limit',
    'system_booking_trip_limit_approved',
    'system_deadline',
    'system_condition_positive',
    'system_condition_negative',
    'system_condition_0',
    'system_condition_1',
    'system_condition_2',
    'system_condition_3',
    'system_condition_4',
    'system_condition_5',
    'system_condition_6',
    'system_condition_7',
    'system_condition_8',
    'system_condition_9',
  ])
  @IsString()
  @IsNotEmpty()
  action_type:
    | 'upload'
    | 'upload_batch'
    | 'accept'
    | 'ukep_sign'
    | 'ukep_sign_batch'
    | 'unep_sign'
    | 'unep_sign_batch'
    | 'pep_sign'
    | 'pep_sign_batch'
    | 'generate_document_from_template'
    | 'decline'
    | 'decline_sign'
    | 'return'
    | 'competency_eval'
    | 'competency_profile'
    | 'system'
    | 'system_validate_sign'
    | 'system_set_vacation_days'
    | 'system_sync_scheduled_vacations'
    | 'system_booking_hook'
    | 'system_booking_approve'
    | 'system_booking_limits_exceeded_approve'
    | 'system_booking_trip_create'
    | 'system_booking_trip_info'
    | 'system_booking_trip_change'
    | 'system_booking_trip_ordering'
    | 'system_booking_trip_limit'
    | 'system_booking_trip_limit_approved'
    | 'system_deadline'
    | 'system_condition_positive'
    | 'system_condition_negative'
    | 'system_condition_0'
    | 'system_condition_1'
    | 'system_condition_2'
    | 'system_condition_3'
    | 'system_condition_4'
    | 'system_condition_5'
    | 'system_condition_6'
    | 'system_condition_7'
    | 'system_condition_8'
    | 'system_condition_9';
}

export class DoAcceptParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class DoReturnParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class DoUploadBatchParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class DoUploadParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class EventNodeValidatorsParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  node_id: string;
}

export class RestoreEventParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class CancelEventParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class GetEventParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class GetEventFlowParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class GetBookingRedirectParams {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class GetAttrOptionsParams {
  @IsString()
  @IsNotEmpty()
  attr_code: string;
}

export class EmployeeCompetencyEventsParams {
  @IsUUID()
  @IsNotEmpty()
  employee_id: string;
}

export class EmployeeCompetencyParams {
  @IsUUID()
  @IsNotEmpty()
  employee_id: string;
}

export class GetCreateEventOptionsEmployeeEventTypesParams {
  @IsString()
  @IsNotEmpty()
  employee_id: string;
}

export class GetEventEmployeesQuery {
  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetEventStaffPositionEmployeesQuery {
  @IsUUID()
  @IsOptional()
  staff_position_id?: string;
}

export class GetEventCompetencyOptionsQuery {
  @IsUUID()
  @IsNotEmpty()
  staff_position_id: string;
}

export class GetEventAttributeOptionsQuery {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  values?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;

  @IsUUID()
  @IsOptional()
  hierarchic_start_item_id?: string;

  @IsBoolean()
  @IsOptional()
  hierarchic_view?: boolean;
}

export class GetScheduleConsecutiveNumberFilterOptionsQuery {
  @IsString()
  @IsNotEmpty()
  schedule_id: string;

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  tags?: string[];
}

export class GetScheduleEventEmployeeFilterOptionsQuery {
  @IsString()
  @IsNotEmpty()
  schedule_id: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetScheduleFavoritesEventsQuery {
  @IsUUID()
  @IsOptional()
  event_id?: string;
}

export class GetEventAttributeFileQuery {
  @IsUUID()
  @IsOptional()
  id?: string;
}

export class GetBookingRedirectQuery {
  @IsString()
  @IsNotEmpty()
  token: string;
}

export class GetCreateSimpleEventTypeOptionsQuery {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetEventTypeGroupFilterOptionsQuery {
  @IsUUID()
  @IsOptional()
  company_id?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  tags?: string[];
}

export class GetDocumentTypeFilterOptionsQuery {
  @IsUUID()
  @IsOptional()
  company_id?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetParentEventFilterOptionsQuery {
  @IsUUID()
  @IsOptional()
  id?: string;

  @IsUUID()
  @IsOptional()
  company_id?: string;

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetEventBatchFilterOptionsQuery {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsUUID()
  @IsOptional()
  company_id?: string;

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetEmployeePositionFilterOptionsQuery {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  names?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetConsecutiveNumberFilterOptionsQuery {
  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetEmployeeFilterOptionsQuery {
  @IsUUID()
  @IsOptional()
  company_id?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetAttrOptionsQuery {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;

  @IsUUID()
  @IsOptional()
  company_id?: string;
}

export class GetAvailableEventFiltersAbsenceQuery {
  @IsUUID()
  @IsOptional()
  company_id?: string;
}

export class GetAvailableEventFiltersQuery {
  @IsIn(['goal_management'])
  @IsString()
  @IsOptional()
  page?: 'goal_management';
}

export class GetCreateEventOptionsEventTypesQuery {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsBoolean()
  @IsOptional()
  batch_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  no_employee?: boolean;

  @IsUUID()
  @IsOptional()
  parent_event_id?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  tag?: string[];

  @IsUUID()
  @IsOptional()
  event_type_group?: string;
}

export class GetEventTypeCreateOptionsQuery {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;

  @IsUUID()
  @IsOptional()
  employee_id?: string;

  @IsUUID()
  @IsOptional()
  parent_event_id?: string;

  @IsBoolean()
  @IsOptional()
  is_batch?: boolean;
}

export class GetCreateEventOptionsEmployeeEventTypesQuery {
  @IsUUID()
  @IsOptional()
  parent_event_id?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  tag?: string[];

  @IsUUID()
  @IsOptional()
  event_type_group?: string;

  @IsString()
  @IsOptional()
  collection_id?: string;
}

export class GetCreateEventOptionsEmployeesQuery {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsBoolean()
  @IsOptional()
  for_another?: boolean;

  @IsUUID()
  @IsOptional()
  unit_id?: string;

  @IsUUID()
  @IsOptional()
  employee_id?: string;

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class EventCreateOptionsEventTypeParticipantOptionsQuery {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;

  @IsUUID()
  @IsNotEmpty()
  role_id: string;

  @IsString()
  @IsOptional()
  search?: string;

  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class GetCreateEventOptionsCompaniesQuery {
  @IsBoolean()
  @IsOptional()
  with_event_types?: boolean;

  @IsUUID()
  @IsOptional()
  event_type_group_id?: string;
}

export class GetCreateEventOptionsCommonQuery {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsUUID()
  @IsOptional()
  parent_event_id?: string;
}

export class GetEventsXlsxQuery {
  @IsIn(['events', 'documents'])
  @IsString()
  @IsNotEmpty()
  type: 'events' | 'documents';
}

export class GetEventBatchEmployeeCountQuery {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;

  @IsUUID()
  @IsOptional()
  parent_event_id?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  unit_ids?: string[];
}

export class GetEventBatchEmployeesQuery {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;

  @IsUUID()
  @IsOptional()
  parent_event_id?: string;

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  unit_ids?: string[];
}

export class GetEventBatchListQuery {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCancelReasonsQuery {
  @IsBoolean()
  @IsOptional()
  is_candidate?: boolean;
}

export type AbsenceGroupNick = string;

export class AbsenceGroup {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn([
    'vacation',
    'maternity_leave',
    'student_leave',
    'business_trip',
    'sick_leave',
  ])
  @IsString()
  @IsNotEmpty()
  nick:
    | 'vacation'
    | 'maternity_leave'
    | 'student_leave'
    | 'business_trip'
    | 'sick_leave';
}

export class EmployeeAbsence {
  @IsDateString()
  @IsNotEmpty()
  from_date: string;

  @IsDateString()
  @IsNotEmpty()
  to_date: string;

  @IsInt()
  @IsNotEmpty()
  days_count: number;

  @ValidateNested()
  @Type(() => AbsenceGroup)
  @IsNotEmpty()
  absence_group: AbsenceGroup;
}

export type SignType = string;

export type CryptoProPluginOpenType = string;

export class CompanyItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  tsp_url?: string;

  @IsIn(['capi', 'cades'])
  @IsString()
  @IsOptional()
  crypto_pro_plugin_open_type?: 'capi' | 'cades';

  @IsIn(['api', 'browser_plugin'])
  @IsString()
  @IsOptional()
  hash_source?: 'api' | 'browser_plugin';
}

export class CompanyEmployee {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  first_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  middle_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  last_name: string;

  @ValidateNested()
  @Type(() => CompanyItem)
  @IsNotEmpty()
  company: CompanyItem;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  email?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  phone?: string;

  @IsString()
  @IsNotEmpty()
  personnel_number: string;

  @IsString()
  @IsOptional()
  position?: string;

  @IsIn([
    'kontur',
    'goskey',
    'cryptopro_simple',
    'cryptopro_local',
    'disabled',
    'ukep',
  ])
  @IsString()
  @IsOptional()
  sign_type?:
    | 'kontur'
    | 'goskey'
    | 'cryptopro_simple'
    | 'cryptopro_local'
    | 'disabled'
    | 'ukep';

  @IsUUID()
  @IsOptional()
  unit_id?: string;

  @IsUUID()
  @IsOptional()
  expanded_unit_id?: string;

  @IsString()
  @IsOptional()
  unit_name?: string;

  @IsString()
  @IsOptional()
  unit_breadcrumbs?: string;

  @IsUUID()
  @IsNotEmpty()
  user_id: string;

  @ValidateNested({ each: true })
  @Type(() => EmployeeAbsence)
  @IsArray()
  @IsOptional()
  absences?: EmployeeAbsence[];

  @IsString()
  @IsOptional()
  org_structure_type?: string;
}

export type UnitManagerSettingApprove = string;

export class CompanyEventTypeItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsBoolean()
  @IsNotEmpty()
  disabled: boolean;
}

export class UnitManagerSetting {
  @ValidateNested()
  @Type(() => CompanyEventTypeItem)
  @IsNotEmpty()
  event_type: CompanyEventTypeItem;

  @IsIn(['always', 'direct_only', 'never'])
  @IsString()
  @IsNotEmpty()
  approve: 'always' | 'direct_only' | 'never';
}

export class Unit {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsUUID()
  @IsOptional()
  parent_id?: string;

  @IsBoolean()
  @IsNotEmpty()
  limited_access: boolean;

  @ValidateNested()
  @Type(() => CompanyEmployee)
  @IsOptional()
  manager?: CompanyEmployee;

  @ValidateNested({ each: true })
  @Type(() => UnitManagerSetting)
  @IsArray()
  @IsOptional()
  manager_settings?: UnitManagerSetting[];

  @ValidateNested({ each: true })
  @Type(() => CompanyEmployee)
  @IsArray()
  @IsOptional()
  assistants?: CompanyEmployee[];

  @IsBoolean()
  @IsNotEmpty()
  is_separate: boolean;

  @IsBoolean()
  @IsOptional()
  has_separate_above?: boolean;
}

export class AbsenceActiveNode {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  actions?: (
    | 'upload'
    | 'generate_document_from_template'
    | 'sign'
    | 'accept'
  )[];

  @IsDateString()
  @IsOptional()
  deadline?: string;
}

export type AbsenceStatus = string;

export class AbsenceItem {
  @IsString()
  @IsNotEmpty()
  type: string;

  @IsString()
  @IsOptional()
  type_group?: string;

  @IsIn([
    'new',
    'event_created',
    'approved',
    'in_progress',
    'completed',
    'canceled',
  ])
  @IsString()
  @IsNotEmpty()
  status:
    | 'new'
    | 'event_created'
    | 'approved'
    | 'in_progress'
    | 'completed'
    | 'canceled';

  @IsDateString()
  @IsNotEmpty()
  from_date: string;

  @IsDateString()
  @IsNotEmpty()
  to_date: string;

  @IsInt()
  @IsNotEmpty()
  days_count: number;

  @IsUUID()
  @IsOptional()
  event_id?: string;

  @IsBoolean()
  @IsNotEmpty()
  is_scheduled_vacation: boolean;

  @IsUUID()
  @IsOptional()
  event_type_id?: string;

  @ValidateNested({ each: true })
  @Type(() => AbsenceActiveNode)
  @IsArray()
  @IsOptional()
  active_nodes?: AbsenceActiveNode[];
}

export type DateType = string;

export class WorkCalendarDate {
  @IsDateString()
  @IsNotEmpty()
  date: string;

  @IsIn(['workday', 'weekend', 'holiday'])
  @IsString()
  @IsNotEmpty()
  type: 'workday' | 'weekend' | 'holiday';
}

export class AbsenceEmployee {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  email?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  phone?: string;

  @IsString()
  @IsNotEmpty()
  personnel_number: string;

  @IsString()
  @IsOptional()
  position?: string;

  @ValidateNested()
  @Type(() => Unit)
  @IsOptional()
  unit?: Unit;

  @ValidateNested({ each: true })
  @Type(() => WorkCalendarDate)
  @IsArray()
  @IsNotEmpty()
  calendar_diff: WorkCalendarDate[];
}

export type AbsenceListGroupType = string;

export class AbsenceListGroup {
  @IsIn(['self', 'colleague', 'manager'])
  @IsString()
  @IsOptional()
  type?: 'self' | 'colleague' | 'manager';

  @ValidateNested()
  @Type(() => AbsenceEmployee)
  @IsNotEmpty()
  employee: AbsenceEmployee;

  @ValidateNested({ each: true })
  @Type(() => AbsenceItem)
  @IsArray()
  @IsNotEmpty()
  absences: AbsenceItem[];

  @ValidateNested({ each: true })
  @Type(() => Unit)
  @IsArray()
  @IsOptional()
  units?: Unit[];
}

export class AbsenceListResponse {
  @ValidateNested({ each: true })
  @Type(() => AbsenceListGroup)
  @IsArray()
  @IsNotEmpty()
  groups: AbsenceListGroup[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class EmployeeItem {
  @IsString()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsOptional()
  personnel_number?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  position?: string;

  @IsString()
  @IsOptional()
  unit_name?: string;
}

export class GetEventEmployeesResponse {
  @ValidateNested({ each: true })
  @Type(() => EmployeeItem)
  @IsArray()
  @IsNotEmpty()
  employees: EmployeeItem[];
}

export class GetEventStaffPositionEmployeesResponse {
  @ValidateNested({ each: true })
  @Type(() => EmployeeItem)
  @IsArray()
  @IsNotEmpty()
  employees: EmployeeItem[];
}

export class CompetencyScaleValue {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsInt()
  @IsNotEmpty()
  value: number;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  indicators?: string[];
}

export class CompetencyItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsString()
  @IsOptional()
  group_name?: string;
}

export class EventCompetencyOption {
  @ValidateNested()
  @Type(() => CompetencyItem)
  @IsNotEmpty()
  competency: CompetencyItem;

  @ValidateNested({ each: true })
  @Type(() => CompetencyScaleValue)
  @IsArray()
  @IsNotEmpty()
  scale_values: CompetencyScaleValue[];

  @ValidateNested()
  @Type(() => CompetencyScaleValue)
  @IsOptional()
  current_value?: CompetencyScaleValue;
}

export class GetEventCompetencyOptionsResponse {
  @ValidateNested({ each: true })
  @Type(() => EventCompetencyOption)
  @IsArray()
  @IsNotEmpty()
  competencies: EventCompetencyOption[];
}

export class EventCatalogAttributesItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  value: string;

  @IsString()
  @IsOptional()
  subtext?: string;

  @IsUUID()
  @IsOptional()
  parent_id?: string;

  @IsBoolean()
  @IsNotEmpty()
  is_hierarchic_group: boolean;

  @IsBoolean()
  @IsNotEmpty()
  system: boolean;

  @IsBoolean()
  @IsNotEmpty()
  has_children: boolean;

  @ValidateNested({ each: true })
  @Type(() => EventCatalogAttributesItem)
  @IsArray()
  @IsNotEmpty()
  children: EventCatalogAttributesItem[];

  @IsString()
  @IsOptional()
  fias_level?: string;
}

export class EventCatalogAttributes {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsInt()
  @IsNotEmpty()
  quantity: number;

  @ValidateNested({ each: true })
  @Type(() => EventCatalogAttributesItem)
  @IsArray()
  @IsNotEmpty()
  items: EventCatalogAttributesItem[];
}

export class EventCompetencyValue {
  @ValidateNested()
  @Type(() => CompetencyScaleValue)
  @IsNotEmpty()
  value: CompetencyScaleValue;

  @IsBoolean()
  @IsNotEmpty()
  is_employee: boolean;
}

export class EventCompetency {
  @ValidateNested()
  @Type(() => CompetencyItem)
  @IsNotEmpty()
  competency: CompetencyItem;

  @ValidateNested({ each: true })
  @Type(() => CompetencyScaleValue)
  @IsArray()
  @IsNotEmpty()
  scale_values: CompetencyScaleValue[];

  @ValidateNested({ each: true })
  @Type(() => EventCompetencyValue)
  @IsArray()
  @IsOptional()
  values?: EventCompetencyValue[];

  @ValidateNested()
  @Type(() => CompetencyScaleValue)
  @IsOptional()
  required_level?: CompetencyScaleValue;

  @ValidateNested()
  @Type(() => CompetencyScaleValue)
  @IsOptional()
  current_value?: CompetencyScaleValue;

  @ValidateNested()
  @Type(() => CompetencyScaleValue)
  @IsOptional()
  new_value?: CompetencyScaleValue;
}

export class EventCompetencyGroup {
  @IsString()
  @IsNotEmpty()
  name: string;

  @ValidateNested({ each: true })
  @Type(() => EventCompetency)
  @IsArray()
  @IsNotEmpty()
  competencies: EventCompetency[];
}

export class VacationAvailableDays {
  @IsUUID()
  @IsNotEmpty()
  vacation_type: string;

  @IsString()
  @IsNotEmpty()
  type_name: string;

  @IsNumber()
  @IsNotEmpty()
  available_days: number;
}

export class EventOptions {
  @IsNumber()
  @IsOptional()
  available_vacation_days?: number;

  @IsString()
  @IsOptional()
  create_child_label?: string;

  @ValidateNested({ each: true })
  @Type(() => VacationAvailableDays)
  @IsArray()
  @IsOptional()
  available_vacation_days_by_types?: VacationAvailableDays[];

  @IsBoolean()
  @IsOptional()
  docx_file_available?: boolean;

  @IsBoolean()
  @IsOptional()
  docx_multiple_files?: boolean;
}

export class UserShortInfo {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  first_name: string;

  @IsString()
  @IsNotEmpty()
  last_name: string;

  @IsString()
  @IsOptional()
  middle_name?: string;
}

export class EventComment {
  @IsDateString()
  @IsNotEmpty()
  created_at: string;

  @ValidateNested()
  @Type(() => UserShortInfo)
  @IsOptional()
  user?: UserShortInfo;

  @IsString()
  @IsOptional()
  info?: string;

  @IsString()
  @IsNotEmpty()
  comment: string;

  @IsString()
  @IsOptional()
  role?: string;

  @IsIn(['system'])
  @IsString()
  @IsOptional()
  type?: 'system';
}

export class EventPermissions {
  @IsBoolean()
  @IsNotEmpty()
  to_paper: boolean;

  @IsBoolean()
  @IsNotEmpty()
  cancel: boolean;

  @IsBoolean()
  @IsNotEmpty()
  create_linked: boolean;
}

export class RoleItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class EventParticipant {
  @ValidateNested()
  @Type(() => RoleItem)
  @IsNotEmpty()
  role: RoleItem;

  @ValidateNested({ each: true })
  @Type(() => UserShortInfo)
  @IsArray()
  @IsNotEmpty()
  user: UserShortInfo[];
}

export class AttributeEmployee {
  @IsUUID()
  @IsNotEmpty()
  value: string;

  @IsString()
  @IsNotEmpty()
  label: string;
}

export class AttributeValueGoal {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsInt()
  @IsNotEmpty()
  share: number;

  @IsBoolean()
  @IsNotEmpty()
  mandatory: boolean;
}

export class AttributeValueGoalsFinalReview {
  @ValidateNested({ each: true })
  @Type(() => AttributeValueGoal)
  @IsArray()
  @IsNotEmpty()
  goals: AttributeValueGoal[];

  @IsInt()
  @IsOptional()
  percentage_manager?: number;

  @IsString()
  @IsOptional()
  justification?: string;
}

export class AttributeValueGoalReview {
  @ValidateNested()
  @Type(() => AttributeValueGoal)
  @IsNotEmpty()
  goal: AttributeValueGoal;

  @IsInt()
  @IsOptional()
  percentage_employee?: number;

  @IsInt()
  @IsOptional()
  percentage_manager?: number;

  @IsString()
  @IsOptional()
  actions?: string;

  @IsString()
  @IsOptional()
  supporting_docs?: string;

  @IsString()
  @IsOptional()
  justification?: string;
}

export class AttributeValueGoalChange {
  @ValidateNested()
  @Type(() => AttributeValueGoal)
  @IsOptional()
  goal?: AttributeValueGoal;

  @IsString()
  @IsOptional()
  new_name?: string;

  @IsInt()
  @IsOptional()
  new_share?: number;

  @IsString()
  @IsOptional()
  change_reason?: string;

  @IsBoolean()
  @IsOptional()
  is_deleted?: boolean;
}

export class AttributeEntity {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsOptional()
  name?: string;
}

export class AttributeFile {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  url: string;
}

export class AttributeCatalog_data {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  catalog_item_ids: string[];
}

export type AttributeType = string;

export class Attribute {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn([
    'text',
    'textarea',
    'date',
    'datetime',
    'file',
    'file_multiple',
    'choice',
    'year',
    'text_catalog',
    'url',
    'text_fixed_catalog',
    'staff_position',
    'goals',
    'goals_change',
    'goals_review',
    'goals_final_review',
    'employee',
    'fns_verification',
    'address_catalog',
    'hierarchic_catalog',
    'orgstructure_catalog',
  ])
  @IsString()
  @IsNotEmpty()
  type:
    | 'text'
    | 'textarea'
    | 'date'
    | 'datetime'
    | 'file'
    | 'file_multiple'
    | 'choice'
    | 'year'
    | 'text_catalog'
    | 'url'
    | 'text_fixed_catalog'
    | 'staff_position'
    | 'goals'
    | 'goals_change'
    | 'goals_review'
    | 'goals_final_review'
    | 'employee'
    | 'fns_verification'
    | 'address_catalog'
    | 'hierarchic_catalog'
    | 'orgstructure_catalog';

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  description?: string;

  @ValidateNested()
  @Type(() => AttributeCatalog_data)
  @IsOptional()
  catalog_data?: AttributeCatalog_data;

  @IsOptional()
  value?:
    | string
    | string[]
    | AttributeFile
    | AttributeFile[]
    | AttributeEntity
    | AttributeValueGoal[]
    | AttributeValueGoalChange[]
    | AttributeValueGoalReview[]
    | AttributeValueGoalsFinalReview
    | AttributeEmployee
    | AttributeEmployee[];
}

export class DocumentSignature {
  @IsString()
  @IsOptional()
  signed_by?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  signed_by_name?: string;

  @IsIn(['not_signed', 'signed', 'on_signing', 'can_sign', 'declined_sign'])
  @IsString()
  @IsNotEmpty()
  status: 'not_signed' | 'signed' | 'on_signing' | 'can_sign' | 'declined_sign';
}

export class Document {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  document_type_name: string;

  @IsString()
  @IsNotEmpty()
  url: string;

  @IsString()
  @IsNotEmpty()
  url_with_stamp: string;

  @IsString()
  @IsNotEmpty()
  url_zip: string;

  @ValidateNested({ each: true })
  @Type(() => DocumentSignature)
  @IsArray()
  @IsNotEmpty()
  signatures: DocumentSignature[];

  @IsDateString()
  @IsOptional()
  read_at?: string;

  @IsBoolean()
  @IsNotEmpty()
  show_read_at: boolean;
}

export class CustomState {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsIn(['gray', 'darkgray', 'green', 'orange', 'purple', 'blue', 'red'])
  @IsString()
  @IsOptional()
  color?: 'gray' | 'darkgray' | 'green' | 'orange' | 'purple' | 'blue' | 'red';
}

export class NodeActionConfirmation {
  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsNotEmpty()
  body: string;
}

export class NodeActionResponsible {
  @IsString()
  @IsNotEmpty()
  role: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class ActionDocumentType {
  @IsUUID()
  @IsNotEmpty()
  node_id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsBoolean()
  @IsNotEmpty()
  required: boolean;

  @IsBoolean()
  @IsNotEmpty()
  info_required: boolean;
}

export class ActionExtra {
  @ValidateNested({ each: true })
  @Type(() => ActionDocumentType)
  @IsArray()
  @IsOptional()
  document_types?: ActionDocumentType[];

  @IsIn(['candidate_notify'])
  @IsString()
  @IsOptional()
  marker?: 'candidate_notify';
}

export class FormMetaObjectAttribute {
  @IsIn(['attribute'])
  @IsString()
  @IsNotEmpty()
  type: 'attribute';

  @IsUUID()
  @IsNotEmpty()
  id: string;
}

export class FormMetaObjectRow {
  @IsIn(['row'])
  @IsString()
  @IsNotEmpty()
  type: 'row';

  @ValidateNested({ each: true })
  @Type(() => FormMetaObjectAttribute)
  @IsArray()
  @IsNotEmpty()
  objects: FormMetaObjectAttribute[];
}

export class FormMetaObjectSubBlock {
  @IsIn(['subblock'])
  @IsString()
  @IsNotEmpty()
  type: 'subblock';

  @IsBoolean()
  @IsNotEmpty()
  is_multiple: boolean;

  @IsBoolean()
  @IsNotEmpty()
  locked: boolean;

  @IsArray()
  @IsNotEmpty()
  objects: (FormMetaObjectRow | FormMetaObjectAttribute)[];
}

export class FormMetaObjectBlock {
  @IsIn(['block'])
  @IsString()
  @IsNotEmpty()
  type: 'block';

  @IsString()
  @IsOptional()
  title?: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsArray()
  @IsNotEmpty()
  objects: (
    | FormMetaObjectSubBlock
    | FormMetaObjectRow
    | FormMetaObjectAttribute
  )[];
}

export class FormMetaObjectDocument {
  @IsIn(['document'])
  @IsString()
  @IsNotEmpty()
  type: 'document';

  @IsString()
  @IsOptional()
  title?: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  classes?: 'half-width'[];

  @IsBoolean()
  @IsNotEmpty()
  is_multiple: boolean;

  @IsBoolean()
  @IsNotEmpty()
  locked: boolean;

  @IsArray()
  @IsNotEmpty()
  objects: (
    | FormMetaObjectBlock
    | FormMetaObjectRow
    | FormMetaObjectAttribute
  )[];
}

export class FormMetaObjectStep {
  @IsIn(['step'])
  @IsString()
  @IsNotEmpty()
  type: 'step';

  @IsString()
  @IsOptional()
  title?: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsArray()
  @IsNotEmpty()
  objects: (FormMetaObjectDocument | FormMetaObjectBlock)[];
}

export class FormAttributeTooltip {
  @IsString()
  @IsOptional()
  title?: string;

  @IsString()
  @IsNotEmpty()
  description: string;

  @IsIn(['inline', 'disclaimer'])
  @IsString()
  @IsNotEmpty()
  style: 'inline' | 'disclaimer';

  @IsBoolean()
  @IsNotEmpty()
  visible: boolean;
}

export class FormAttributeInfoVacationDays {
  @IsIn(['vacation_days'])
  @IsString()
  @IsNotEmpty()
  type: 'vacation_days';

  @IsNumber()
  @IsOptional()
  vacation_days?: number;

  @IsBoolean()
  @IsNotEmpty()
  import_vacation_days: boolean;
}

export class FormAttributeCondition {
  @IsUUID()
  @IsNotEmpty()
  attribute_id: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  equal?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  unequal?: string[];
}

export class FormAttributeShowWhen {
  @ValidateNested({ each: true })
  @Type(() => FormAttributeCondition)
  @IsArray()
  @IsOptional()
  conditions?: FormAttributeCondition[];
}

export class FormAttributeChoice {
  @IsString()
  @IsNotEmpty()
  label: string;

  @IsString()
  @IsNotEmpty()
  value: string;

  @IsBoolean()
  @IsOptional()
  default?: boolean;
}

export class FormAttributeOptions {
  @ValidateNested({ each: true })
  @Type(() => FormAttributeChoice)
  @IsArray()
  @IsOptional()
  choices?: FormAttributeChoice[];

  @IsBoolean()
  @IsOptional()
  expanded?: boolean;

  @ValidateNested()
  @Type(() => FormAttributeCondition)
  @IsOptional()
  hide_when?: FormAttributeCondition;

  @ValidateNested()
  @Type(() => FormAttributeShowWhen)
  @IsOptional()
  show_when?: FormAttributeShowWhen;

  @ValidateNested()
  @Type(() => FormAttributeInfoVacationDays)
  @IsOptional()
  info?: FormAttributeInfoVacationDays;

  @ValidateNested()
  @Type(() => FormAttributeTooltip)
  @IsOptional()
  tooltip?: FormAttributeTooltip;

  @IsString()
  @IsOptional()
  placeholder?: string;

  @IsString()
  @IsOptional()
  format?: string;

  @IsIn(['S', 'M', 'L'])
  @IsString()
  @IsOptional()
  size?: 'S' | 'M' | 'L';

  @IsBoolean()
  @IsOptional()
  ocr?: boolean;

  @IsBoolean()
  @IsOptional()
  qrcode?: boolean;

  @IsBoolean()
  @IsOptional()
  is_multiple?: boolean;

  @IsBoolean()
  @IsOptional()
  single_selection?: boolean;

  @IsString()
  @IsOptional()
  expression?: string;

  @IsOptional()
  expression_aliases?: object;

  @IsString()
  @IsOptional()
  alias?: string;

  @IsInt()
  @IsOptional()
  decimals?: number;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  editable_fields?: (
    | 'percentage_employee'
    | 'percentage_manager'
    | 'actions'
    | 'obstacles'
    | 'supporting_docs'
    | 'justification'
  )[];

  @IsInt()
  @IsOptional()
  max_length?: number;

  @IsBoolean()
  @IsOptional()
  select_leaf_only?: boolean;

  @IsBoolean()
  @IsOptional()
  validate_not_editable?: boolean;
}

export class FormAttribute {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn([
    'text',
    'textarea',
    'date',
    'datetime',
    'file',
    'file_multiple',
    'choice',
    'year',
    'text_catalog',
    'url',
    'text_fixed_catalog',
    'staff_position',
    'goals',
    'goals_change',
    'goals_review',
    'goals_final_review',
    'employee',
    'fns_verification',
    'address_catalog',
    'hierarchic_catalog',
    'orgstructure_catalog',
  ])
  @IsString()
  @IsNotEmpty()
  type:
    | 'text'
    | 'textarea'
    | 'date'
    | 'datetime'
    | 'file'
    | 'file_multiple'
    | 'choice'
    | 'year'
    | 'text_catalog'
    | 'url'
    | 'text_fixed_catalog'
    | 'staff_position'
    | 'goals'
    | 'goals_change'
    | 'goals_review'
    | 'goals_final_review'
    | 'employee'
    | 'fns_verification'
    | 'address_catalog'
    | 'hierarchic_catalog'
    | 'orgstructure_catalog';

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsBoolean()
  @IsNotEmpty()
  required: boolean;

  @IsBoolean()
  @IsOptional()
  read_only?: boolean;

  @ValidateNested()
  @Type(() => FormAttributeOptions)
  @IsOptional()
  options?: FormAttributeOptions;
}

export type UnepType = string;

export class NodeAction {
  @IsIn([
    'kontur',
    'goskey',
    'cryptopro_simple',
    'cryptopro_local',
    'disabled',
    'ukep',
  ])
  @IsString()
  @IsNotEmpty()
  unep_type:
    | 'kontur'
    | 'goskey'
    | 'cryptopro_simple'
    | 'cryptopro_local'
    | 'disabled'
    | 'ukep';

  @IsIn(['sms', 'no_sms'])
  @IsString()
  @IsOptional()
  two_factor_auth?: 'sms' | 'no_sms';

  @IsIn(['new', 'processing', 'canceled', 'error'])
  @IsString()
  @IsOptional()
  signature_status?: 'new' | 'processing' | 'canceled' | 'error';

  @IsIn(['snils_not_found'])
  @IsString()
  @IsOptional()
  signature_detailed_error?: 'snils_not_found';

  @IsString()
  @IsOptional()
  name?: string;

  @IsBoolean()
  @IsNotEmpty()
  is_primary: boolean;

  @IsBoolean()
  @IsOptional()
  is_employee?: boolean;

  @IsIn([
    'upload',
    'upload_batch',
    'accept',
    'ukep_sign',
    'ukep_sign_batch',
    'unep_sign',
    'unep_sign_batch',
    'pep_sign',
    'pep_sign_batch',
    'generate_document_from_template',
    'decline',
    'decline_sign',
    'return',
    'competency_eval',
    'competency_profile',
    'system',
    'system_validate_sign',
    'system_set_vacation_days',
    'system_sync_scheduled_vacations',
    'system_booking_hook',
    'system_booking_approve',
    'system_booking_limits_exceeded_approve',
    'system_booking_trip_create',
    'system_booking_trip_info',
    'system_booking_trip_change',
    'system_booking_trip_ordering',
    'system_booking_trip_limit',
    'system_booking_trip_limit_approved',
    'system_deadline',
    'system_condition_positive',
    'system_condition_negative',
    'system_condition_0',
    'system_condition_1',
    'system_condition_2',
    'system_condition_3',
    'system_condition_4',
    'system_condition_5',
    'system_condition_6',
    'system_condition_7',
    'system_condition_8',
    'system_condition_9',
  ])
  @IsString()
  @IsNotEmpty()
  type:
    | 'upload'
    | 'upload_batch'
    | 'accept'
    | 'ukep_sign'
    | 'ukep_sign_batch'
    | 'unep_sign'
    | 'unep_sign_batch'
    | 'pep_sign'
    | 'pep_sign_batch'
    | 'generate_document_from_template'
    | 'decline'
    | 'decline_sign'
    | 'return'
    | 'competency_eval'
    | 'competency_profile'
    | 'system'
    | 'system_validate_sign'
    | 'system_set_vacation_days'
    | 'system_sync_scheduled_vacations'
    | 'system_booking_hook'
    | 'system_booking_approve'
    | 'system_booking_limits_exceeded_approve'
    | 'system_booking_trip_create'
    | 'system_booking_trip_info'
    | 'system_booking_trip_change'
    | 'system_booking_trip_ordering'
    | 'system_booking_trip_limit'
    | 'system_booking_trip_limit_approved'
    | 'system_deadline'
    | 'system_condition_positive'
    | 'system_condition_negative'
    | 'system_condition_0'
    | 'system_condition_1'
    | 'system_condition_2'
    | 'system_condition_3'
    | 'system_condition_4'
    | 'system_condition_5'
    | 'system_condition_6'
    | 'system_condition_7'
    | 'system_condition_8'
    | 'system_condition_9';

  @ValidateNested()
  @Type(() => Document)
  @IsOptional()
  document?: Document;

  @ValidateNested({ each: true })
  @Type(() => Document)
  @IsArray()
  @IsOptional()
  documents?: Document[];

  @ValidateNested({ each: true })
  @Type(() => FormAttribute)
  @IsArray()
  @IsNotEmpty()
  form_attributes: FormAttribute[];

  @IsArray()
  @IsOptional()
  form_meta?: (
    | FormMetaObjectStep
    | FormMetaObjectDocument
    | FormMetaObjectBlock
  )[];

  @ValidateNested()
  @Type(() => ActionExtra)
  @IsOptional()
  extra?: ActionExtra;

  @ValidateNested({ each: true })
  @Type(() => NodeActionResponsible)
  @IsArray()
  @IsNotEmpty()
  responsible: NodeActionResponsible[];

  @ValidateNested()
  @Type(() => NodeActionConfirmation)
  @IsOptional()
  confirmation?: NodeActionConfirmation;
}

export class EventNode {
  @IsUUID()
  @IsNotEmpty()
  node_id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  description?: string;

  @ValidateNested({ each: true })
  @Type(() => NodeAction)
  @IsArray()
  @IsNotEmpty()
  actions: NodeAction[];

  @IsDateString()
  @IsOptional()
  deadline?: string;

  @ValidateNested()
  @Type(() => CustomState)
  @IsOptional()
  custom_state?: CustomState;
}

export class CanceledDetails {
  @IsDateString()
  @IsNotEmpty()
  canceled_at: string;

  @IsString()
  @IsNotEmpty()
  reason: string;

  @IsIn([
    'node_deadline',
    'event_deadline',
    'manual_employee',
    'manual_company',
    'dismiss',
    'workflow_change',
  ])
  @IsString()
  @IsNotEmpty()
  reason_type:
    | 'node_deadline'
    | 'event_deadline'
    | 'manual_employee'
    | 'manual_company'
    | 'dismiss'
    | 'workflow_change';

  @IsString()
  @IsOptional()
  document_type_name?: string;

  @IsString()
  @IsNotEmpty()
  node_name: string;

  @IsString()
  @IsNotEmpty()
  responsible: string;

  @IsBoolean()
  @IsNotEmpty()
  can_restore: boolean;

  @IsString()
  @IsOptional()
  comment?: string;
}

export class Action {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  retry_text?: string;

  @IsIn([
    'upload',
    'accept',
    'ukep_sign',
    'ukep_sign_batch',
    'unep_sign',
    'unep_sign_batch',
    'pep_sign',
    'pep_sign_batch',
    'system',
    'system_validate_sign',
    'system_delete_attributes',
    'generate_document_from_template',
    'decline',
    'decline_sign',
    'canceled_to_paper',
    'in_paper',
    'completed',
    'canceled',
    'declined',
    'declined_sign',
    'competency_eval',
    'competency_profile',
    'system_complete_candidate',
    'system_booking_hook',
    'system_booking_approve',
    'system_booking_limits_exceeded_approve',
    'system_booking_trip_create',
    'system_booking_trip_info',
    'system_booking_trip_change',
    'system_booking_trip_cancel',
    'system_booking_trip_ordering',
    'system_booking_trip_limit',
    'system_booking_trip_limit_approved',
    'system_set_vacation_days',
    'system_sync_scheduled_vacations',
    'system_deadline',
  ])
  @IsString()
  @IsNotEmpty()
  type:
    | 'upload'
    | 'accept'
    | 'ukep_sign'
    | 'ukep_sign_batch'
    | 'unep_sign'
    | 'unep_sign_batch'
    | 'pep_sign'
    | 'pep_sign_batch'
    | 'system'
    | 'system_validate_sign'
    | 'system_delete_attributes'
    | 'generate_document_from_template'
    | 'decline'
    | 'decline_sign'
    | 'canceled_to_paper'
    | 'in_paper'
    | 'completed'
    | 'canceled'
    | 'declined'
    | 'declined_sign'
    | 'competency_eval'
    | 'competency_profile'
    | 'system_complete_candidate'
    | 'system_booking_hook'
    | 'system_booking_approve'
    | 'system_booking_limits_exceeded_approve'
    | 'system_booking_trip_create'
    | 'system_booking_trip_info'
    | 'system_booking_trip_change'
    | 'system_booking_trip_cancel'
    | 'system_booking_trip_ordering'
    | 'system_booking_trip_limit'
    | 'system_booking_trip_limit_approved'
    | 'system_set_vacation_days'
    | 'system_sync_scheduled_vacations'
    | 'system_deadline';
}

export class ActiveNode {
  @IsUUID()
  @IsNotEmpty()
  node_id: string;

  @ValidateNested()
  @Type(() => Action)
  @IsNotEmpty()
  action: Action;

  @ValidateNested()
  @Type(() => CustomState)
  @IsOptional()
  custom_state?: CustomState;

  @IsString()
  @IsOptional()
  responsible?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  document_type_names?: string[];
}

export type Email = string;

export type Phone = string;

export class EventEmployee {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  personnel_number: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsDateString()
  @IsNotEmpty()
  birthday: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  first_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  second_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  middle_name: string;

  @IsString()
  @IsOptional()
  unit?: string;

  @IsString()
  @IsOptional()
  legal_unit?: string;

  @IsString()
  @IsOptional()
  position?: string;

  @IsDateString()
  @IsOptional()
  dismissed_at?: string;

  @IsString()
  @IsOptional()
  phone?: string;

  @IsString()
  @IsOptional()
  email?: string;
}

export class TimeRange {
  @IsDateString()
  @IsOptional()
  start?: string;

  @IsDateString()
  @IsOptional()
  end?: string;
}

export class EventTypeItemChild_button {
  @IsBoolean()
  @IsNotEmpty()
  is_active: boolean;

  @ValidateNested({ each: true })
  @Type(() => TimeRange)
  @IsArray()
  @IsNotEmpty()
  active_periods: TimeRange[];
}

export type EventCategory = string;

export class EventTypeItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['item'])
  @IsString()
  @IsNotEmpty()
  type: 'item';

  @IsString()
  @IsNotEmpty()
  custom_type: string;

  @IsIn(['employee', 'candidate', 'vacation'])
  @IsString()
  @IsOptional()
  category?: 'employee' | 'candidate' | 'vacation';

  @IsBoolean()
  @IsOptional()
  document_info_required?: boolean;

  @IsBoolean()
  @IsNotEmpty()
  disabled: boolean;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  tags?: string[];

  @ValidateNested()
  @Type(() => EventTypeItemChild_button)
  @IsOptional()
  child_button?: EventTypeItemChild_button;
}

export class Event {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => EventTypeItem)
  @IsNotEmpty()
  event_type: EventTypeItem;

  @ValidateNested()
  @Type(() => EventEmployee)
  @IsOptional()
  employee?: EventEmployee;

  @ValidateNested()
  @Type(() => CompanyItem)
  @IsNotEmpty()
  company: CompanyItem;

  @IsDateString()
  @IsNotEmpty()
  created_at: string;

  @ValidateNested({ each: true })
  @Type(() => ActiveNode)
  @IsArray()
  @IsNotEmpty()
  active_nodes: ActiveNode[];

  @IsDateString()
  @IsOptional()
  deadline?: string;

  @ValidateNested()
  @Type(() => CanceledDetails)
  @IsOptional()
  canceled?: CanceledDetails;

  @IsBoolean()
  @IsNotEmpty()
  signed_after_cancel: boolean;

  @IsBoolean()
  @IsNotEmpty()
  signed_after_skip: boolean;

  @IsUUID()
  @IsOptional()
  parent_event_id?: string;

  @IsInt()
  @IsOptional()
  children_events_count?: number;

  @ValidateNested({ each: true })
  @Type(() => EventNode)
  @IsArray()
  @IsNotEmpty()
  nodes: EventNode[];

  @ValidateNested({ each: true })
  @Type(() => EventNode)
  @IsArray()
  @IsNotEmpty()
  current_nodes: EventNode[];

  @ValidateNested({ each: true })
  @Type(() => Document)
  @IsArray()
  @IsNotEmpty()
  documents: Document[];

  @ValidateNested({ each: true })
  @Type(() => Attribute)
  @IsArray()
  @IsNotEmpty()
  attributes: Attribute[];

  @IsArray()
  @IsOptional()
  attributes_meta?: (FormMetaObjectDocument | FormMetaObjectBlock)[];

  @ValidateNested({ each: true })
  @Type(() => EventParticipant)
  @IsArray()
  @IsOptional()
  participants?: EventParticipant[];

  @ValidateNested()
  @Type(() => EventPermissions)
  @IsNotEmpty()
  permissions: EventPermissions;

  @ValidateNested({ each: true })
  @Type(() => EventComment)
  @IsArray()
  @IsOptional()
  comments?: EventComment[];

  @ValidateNested()
  @Type(() => EventOptions)
  @IsOptional()
  options?: EventOptions;

  @ValidateNested({ each: true })
  @Type(() => EventCompetencyGroup)
  @IsArray()
  @IsOptional()
  competency_groups?: EventCompetencyGroup[];

  @IsString()
  @IsOptional()
  consecutive_number?: string;
}

export class ScheduleEvent {
  @ValidateNested()
  @Type(() => Event)
  @IsNotEmpty()
  event: Event;

  @IsInt()
  @IsNotEmpty()
  schedule_year: number;

  @IsUUID()
  @IsNotEmpty()
  schedule_id: string;

  @IsBoolean()
  @IsNotEmpty()
  favorites_allowed: boolean;
}

export class FilterSelectBadge {
  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsIn(['blue', 'velvet', 'red'])
  @IsString()
  @IsOptional()
  color?: 'blue' | 'velvet' | 'red';

  @IsIn(['left', 'bottom'])
  @IsString()
  @IsOptional()
  position?: 'left' | 'bottom';
}

export class FilterSelectOption {
  @IsString()
  @IsNotEmpty()
  value: string;

  @IsString()
  @IsNotEmpty()
  label: string;

  @IsIn(['item'])
  @IsString()
  @IsOptional()
  type?: 'item';

  @ValidateNested()
  @Type(() => FilterSelectBadge)
  @IsOptional()
  badge?: FilterSelectBadge;
}

export class FilterGroupOption {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['group'])
  @IsString()
  @IsNotEmpty()
  type: 'group';

  @IsString()
  @IsNotEmpty()
  label: string;

  @ValidateNested({ each: true })
  @Type(() => FilterSelectOption)
  @IsArray()
  @IsNotEmpty()
  items: FilterSelectOption[];
}

export class VacationScheduleAvailableFiltersConsecutiveNumberOptionsResponse {}

export type VacationScheduleAvailableFiltersEmployeeOptionsResponse =
  FilterSelectOption[];

export class FilterTimeRange {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['timerange'])
  @IsString()
  @IsNotEmpty()
  type: 'timerange';

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsIn(['till_now', 'any', 'from_now'])
  @IsString()
  @IsNotEmpty()
  allow_dates: 'till_now' | 'any' | 'from_now';

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterBool {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['bool'])
  @IsString()
  @IsNotEmpty()
  type: 'bool';

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;

  @IsString()
  @IsOptional()
  tooltip?: string;
}

export class FilterMultipleSearch {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['multiple-search'])
  @IsString()
  @IsNotEmpty()
  type: 'multiple-search';

  @IsString()
  @IsNotEmpty()
  url: string;

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterSearch {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['search'])
  @IsString()
  @IsNotEmpty()
  type: 'search';

  @IsString()
  @IsNotEmpty()
  url: string;

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  disable_if_one_option?: boolean;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterText {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['text'])
  @IsString()
  @IsNotEmpty()
  type: 'text';

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterMultiple {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['multiple'])
  @IsString()
  @IsNotEmpty()
  type: 'multiple';

  @ValidateNested({ each: true })
  @Type(() => FilterSelectOption)
  @IsArray()
  @IsNotEmpty()
  options: FilterSelectOption[];

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  disable_if_one_option?: boolean;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class FilterSelect {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['select'])
  @IsString()
  @IsNotEmpty()
  type: 'select';

  @ValidateNested({ each: true })
  @Type(() => FilterSelectOption)
  @IsArray()
  @IsNotEmpty()
  options: FilterSelectOption[];

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  autocomplete?: boolean;

  @IsBoolean()
  @IsOptional()
  disable_if_one_option?: boolean;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class ScheduleEventAvailableFilters {
  @IsArray()
  @IsNotEmpty()
  filters: (
    | FilterSelect
    | FilterMultiple
    | FilterText
    | FilterSearch
    | FilterMultipleSearch
    | FilterBool
    | FilterTimeRange
  )[];
}

export class ScheduleEmployee {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  personnel_number: string;

  @IsString()
  @IsNotEmpty()
  position: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  first_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  second_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  middle_name: string;
}

export class ScheduleManager {
  @ValidateNested()
  @Type(() => ScheduleEmployee)
  @IsNotEmpty()
  employee: ScheduleEmployee;

  @IsString()
  @IsNotEmpty()
  type_name: string;
}

export class UnitScheduleItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsUUID()
  @IsOptional()
  parent_id?: string;

  @ValidateNested()
  @Type(() => ScheduleManager)
  @IsOptional()
  manager?: ScheduleManager;
}

export class VacationItem {
  @IsString()
  @IsNotEmpty()
  type_name: string;

  @IsDateString()
  @IsNotEmpty()
  from_date: string;

  @IsDateString()
  @IsNotEmpty()
  to_date: string;
}

export class ScheduleEventParticipant {
  @IsString()
  @IsNotEmpty()
  type: string;

  @IsString()
  @IsOptional()
  data?: string;
}

export class ReturnEventAction {
  @IsUUID()
  @IsNotEmpty()
  node_id: string;

  @IsUUID()
  @IsOptional()
  message_attr_id?: string;
}

export class AcceptEventAction {
  @IsUUID()
  @IsNotEmpty()
  node_id: string;

  @IsUUID()
  @IsOptional()
  message_attr_id?: string;
}

export class EventActions {
  @ValidateNested()
  @Type(() => AcceptEventAction)
  @IsOptional()
  accept_action?: AcceptEventAction;

  @ValidateNested()
  @Type(() => ReturnEventAction)
  @IsOptional()
  return_action?: ReturnEventAction;
}

export class ScheduleEventListItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => ScheduleEmployee)
  @IsNotEmpty()
  employee: ScheduleEmployee;

  @IsDateString()
  @IsOptional()
  deadline?: string;

  @ValidateNested()
  @Type(() => EventActions)
  @IsOptional()
  actions?: EventActions;

  @ValidateNested()
  @Type(() => ScheduleEventParticipant)
  @IsOptional()
  active_participant?: ScheduleEventParticipant;

  @IsBoolean()
  @IsNotEmpty()
  up_to_me: boolean;

  @IsBoolean()
  @IsNotEmpty()
  my_event: boolean;

  @IsBoolean()
  @IsNotEmpty()
  favorite: boolean;

  @IsUUID()
  @IsOptional()
  unit_id?: string;

  @ValidateNested({ each: true })
  @Type(() => VacationItem)
  @IsArray()
  @IsNotEmpty()
  vacations: VacationItem[];

  @ValidateNested({ each: true })
  @Type(() => WorkCalendarDate)
  @IsArray()
  @IsOptional()
  calendar_diff?: WorkCalendarDate[];

  @IsIn(['new', 'accept', 'finished', 'canceled', 'onec_sync'])
  @IsString()
  @IsNotEmpty()
  status: 'new' | 'accept' | 'finished' | 'canceled' | 'onec_sync';

  @IsString()
  @IsOptional()
  consecutive_number?: string;
}

export class ScheduleFavoriteEventsListResponse {
  @ValidateNested({ each: true })
  @Type(() => ScheduleEventListItem)
  @IsArray()
  @IsNotEmpty()
  events: ScheduleEventListItem[];

  @ValidateNested({ each: true })
  @Type(() => UnitScheduleItem)
  @IsArray()
  @IsOptional()
  units?: UnitScheduleItem[];

  @IsBoolean()
  @IsNotEmpty()
  show_units: boolean;
}

export class ScheduleEventsXlsxResponse {
  @IsUUID()
  @IsNotEmpty()
  download_id: string;
}

export class ScheduleEventsListResponse {
  @ValidateNested({ each: true })
  @Type(() => ScheduleEventListItem)
  @IsArray()
  @IsNotEmpty()
  events: ScheduleEventListItem[];

  @ValidateNested({ each: true })
  @Type(() => UnitScheduleItem)
  @IsArray()
  @IsOptional()
  units?: UnitScheduleItem[];

  @IsBoolean()
  @IsNotEmpty()
  show_units: boolean;

  @IsInt()
  @IsNotEmpty()
  total_count: number;

  @IsInt()
  @IsOptional()
  total_count_my?: number;

  @IsInt()
  @IsOptional()
  total_count_subordinates?: number;

  @IsInt()
  @IsOptional()
  total_count_all?: number;

  @IsInt()
  @IsOptional()
  total_count_favorites?: number;
}

export class EventAttributeCacheFileResponse {
  @IsUUID()
  @IsNotEmpty()
  file_id: string;

  @IsDateString()
  @IsNotEmpty()
  expired_at: string;
}

export class EventFileHash {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @IsUUID()
  @IsNotEmpty()
  document_id: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  hash: string;
}

export class GetEventFileHashesResponse {
  @ValidateNested({ each: true })
  @Type(() => EventFileHash)
  @IsArray()
  @IsNotEmpty()
  hashes: EventFileHash[];
}

export class GetEventDocumentFileHashResponse {
  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  hash: string;
}

export class CreateEventBatchDocumentZipResponse {
  @IsUUID()
  @IsNotEmpty()
  download_id: string;
}

export class RequestEventDocumentDocxResponse {
  @IsUUID()
  @IsNotEmpty()
  download_id: string;
}

export class BatchEventOperationResponse {
  @IsInt()
  @IsNotEmpty()
  success_count: number;

  @IsInt()
  @IsNotEmpty()
  error_count: number;
}

export class BatchEventAcceptResponse {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  successes: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  errors: string[];
}

export class OnlyCurrentYearValidator {
  @IsIn(['only_current_year'])
  @IsString()
  @IsNotEmpty()
  type: 'only_current_year';

  @IsString()
  @IsOptional()
  from_attribute_id?: string;

  @IsString()
  @IsOptional()
  to_attribute_id?: string;
}

export type Operation = string;

export class CheckValueValidator {
  @IsIn(['check_value'])
  @IsString()
  @IsNotEmpty()
  type: 'check_value';

  @IsString()
  @IsNotEmpty()
  attribute_id: string;

  @IsIn(['eq', 'ne', 'gt', 'lt', 'ge', 'le'])
  @IsString()
  @IsNotEmpty()
  operation: 'eq' | 'ne' | 'gt' | 'lt' | 'ge' | 'le';

  @IsString()
  @IsNotEmpty()
  value: string;
}

export class ValuesCompareValidator {
  @IsIn(['values_compare'])
  @IsString()
  @IsNotEmpty()
  type: 'values_compare';

  @IsString()
  @IsNotEmpty()
  left_attribute_id: string;

  @IsString()
  @IsNotEmpty()
  right_attribute_id: string;

  @IsIn(['eq', 'ne', 'gt', 'lt', 'ge', 'le'])
  @IsString()
  @IsNotEmpty()
  operation: 'eq' | 'ne' | 'gt' | 'lt' | 'ge' | 'le';
}

export class ExceedingLimitValidator {
  @IsIn(['exceeding_limit'])
  @IsString()
  @IsNotEmpty()
  type: 'exceeding_limit';

  @IsString()
  @IsNotEmpty()
  from_attribute_id: string;

  @IsString()
  @IsNotEmpty()
  to_attribute_id: string;

  @IsInt()
  @IsNotEmpty()
  vacation_available_days: number;
}

export class AbsenceOverlapItem {
  @IsDateString()
  @IsNotEmpty()
  from_date: string;

  @IsDateString()
  @IsNotEmpty()
  to_date: string;

  @IsIn(['error', 'warning'])
  @IsString()
  @IsNotEmpty()
  action: 'error' | 'warning';

  @IsString()
  @IsNotEmpty()
  message: string;
}

export class AbsenceOverlapValidator {
  @IsIn(['has_absence'])
  @IsString()
  @IsNotEmpty()
  type: 'has_absence';

  @IsUUID()
  @IsNotEmpty()
  from_attribute_id: string;

  @IsUUID()
  @IsNotEmpty()
  to_attribute_id: string;

  @ValidateNested({ each: true })
  @Type(() => AbsenceOverlapItem)
  @IsArray()
  @IsNotEmpty()
  existing_dates: AbsenceOverlapItem[];
}

export class NonWorkingDaysIncludedCheckValidator {
  @IsIn(['non_working_days_included_check'])
  @IsString()
  @IsNotEmpty()
  type: 'non_working_days_included_check';

  @IsString()
  @IsNotEmpty()
  from_attribute_id: string;

  @IsString()
  @IsNotEmpty()
  to_attribute_id: string;
}

export class VacationPeriodsValidator {
  @IsIn(['vacation_periods'])
  @IsString()
  @IsOptional()
  type?: 'vacation_periods';

  @IsString()
  @IsOptional()
  from_attribute_id?: string;

  @IsString()
  @IsOptional()
  to_attribute_id?: string;

  @IsString()
  @IsOptional()
  type_attribute_id?: string;

  @IsOptional()
  vacations?: object;
}

export class VacationMin5daysBetweenNotWorkdaysValidator {
  @IsIn(['vacation_min_5_days_between_not_workdays'])
  @IsString()
  @IsOptional()
  type?: 'vacation_min_5_days_between_not_workdays';

  @IsString()
  @IsOptional()
  from_attribute_id?: string;

  @IsString()
  @IsOptional()
  to_attribute_id?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  vacation_types?: string[];
}

export class VacationLimitToCalculatedDaysValidator {
  @IsIn(['vacation_limit_to_calculated_days'])
  @IsString()
  @IsOptional()
  type?: 'vacation_limit_to_calculated_days';

  @IsString()
  @IsOptional()
  from_attribute_id?: string;

  @IsString()
  @IsOptional()
  to_attribute_id?: string;
}

export class VacationSchedule14DaysValidator {
  @IsIn(['vacation_schedule_14_days'])
  @IsString()
  @IsOptional()
  type?: 'vacation_schedule_14_days';

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  vacation_types?: string[];

  @IsString()
  @IsOptional()
  from_attribute_id?: string;

  @IsString()
  @IsOptional()
  to_attribute_id?: string;
}

export class VacationScheduleAllDaysValidator {
  @IsIn(['vacation_schedule_all_days'])
  @IsString()
  @IsOptional()
  type?: 'vacation_schedule_all_days';

  @IsString()
  @IsOptional()
  from_attribute_id?: string;

  @IsString()
  @IsOptional()
  to_attribute_id?: string;
}

export class MinHiringDayValidator {
  @IsIn(['min_hiring_day'])
  @IsString()
  @IsNotEmpty()
  type: 'min_hiring_day';

  @IsDateString()
  @IsOptional()
  hired_at?: string;

  @IsIn(['eq', 'ne', 'gt', 'lt', 'ge', 'le'])
  @IsString()
  @IsNotEmpty()
  operation: 'eq' | 'ne' | 'gt' | 'lt' | 'ge' | 'le';

  @IsString()
  @IsNotEmpty()
  attribute_id: string;
}

export class MinSpecificWeekendDaysValidator {
  @IsIn(['min_specific_weekend_days'])
  @IsString()
  @IsNotEmpty()
  type: 'min_specific_weekend_days';

  @IsString()
  @IsNotEmpty()
  from_attribute_id: string;

  @IsString()
  @IsNotEmpty()
  to_attribute_id: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  vacation_types?: string[];
}

export class MinWeekendDaysValidator {
  @IsIn(['min_weekend_days'])
  @IsString()
  @IsNotEmpty()
  type: 'min_weekend_days';

  @IsInt()
  @IsNotEmpty()
  working_days: number;

  @IsInt()
  @IsNotEmpty()
  min_weekend_days: number;

  @IsString()
  @IsNotEmpty()
  from_attribute_id: string;

  @IsString()
  @IsNotEmpty()
  to_attribute_id: string;

  @IsNotEmpty()
  vacation_type: object;
}

export class MaxYearValidator {
  @IsIn(['max_year'])
  @IsString()
  @IsNotEmpty()
  type: 'max_year';

  @IsInt()
  @IsNotEmpty()
  max_year: number;

  @IsInt()
  @IsNotEmpty()
  min_year: number;

  @IsString()
  @IsNotEmpty()
  attribute_id: string;
}

export class DivisibleByValidator {
  @IsIn(['divisible_by'])
  @IsString()
  @IsNotEmpty()
  type: 'divisible_by';

  @IsInt()
  @IsNotEmpty()
  divisible_by: number;

  @IsString()
  @IsNotEmpty()
  from_attribute_id: string;

  @IsString()
  @IsNotEmpty()
  to_attribute_id: string;
}

export class VacationAvailableValidator {
  @IsIn(['vacation_available'])
  @IsString()
  @IsNotEmpty()
  type: 'vacation_available';

  @IsNumber()
  @IsNotEmpty()
  vacation_available_days: number;

  @IsString()
  @IsNotEmpty()
  from_attribute_id: string;

  @IsString()
  @IsNotEmpty()
  to_attribute_id: string;
}

export class SameMonthValidator {
  @IsIn(['same_month'])
  @IsString()
  @IsNotEmpty()
  type: 'same_month';

  @IsString()
  @IsNotEmpty()
  from_attribute_id: string;

  @IsString()
  @IsNotEmpty()
  to_attribute_id: string;
}

export class DateRange {
  @IsDateString()
  @IsNotEmpty()
  from_date: string;

  @IsDateString()
  @IsNotEmpty()
  to_date: string;
}

export class ForbiddenPeriodsValidator {
  @IsIn(['forbidden_periods'])
  @IsString()
  @IsNotEmpty()
  type: 'forbidden_periods';

  @ValidateNested({ each: true })
  @Type(() => DateRange)
  @IsArray()
  @IsNotEmpty()
  existing_dates: DateRange[];

  @IsString()
  @IsNotEmpty()
  from_attribute_id: string;

  @IsString()
  @IsNotEmpty()
  to_attribute_id: string;
}

export class NoOverlapValidator {
  @IsIn(['no_overlap'])
  @IsString()
  @IsNotEmpty()
  type: 'no_overlap';

  @ValidateNested({ each: true })
  @Type(() => DateRange)
  @IsArray()
  @IsNotEmpty()
  existing_dates: DateRange[];

  @IsString()
  @IsNotEmpty()
  from_attribute_id: string;

  @IsString()
  @IsNotEmpty()
  to_attribute_id: string;
}

export class MaximumDurationValidator {
  @IsIn(['max_duration'])
  @IsString()
  @IsNotEmpty()
  type: 'max_duration';

  @IsInt()
  @IsNotEmpty()
  days_count: number;

  @IsBoolean()
  @IsNotEmpty()
  working_days_only: boolean;

  @IsOptional()
  by_vacation_type?: object;

  @IsString()
  @IsNotEmpty()
  from_attribute_id: string;

  @IsString()
  @IsNotEmpty()
  to_attribute_id: string;
}

export class MinimumDurationValidator {
  @IsIn(['min_duration'])
  @IsString()
  @IsNotEmpty()
  type: 'min_duration';

  @IsInt()
  @IsNotEmpty()
  days_count: number;

  @IsBoolean()
  @IsNotEmpty()
  working_days_only: boolean;

  @IsOptional()
  by_vacation_type?: object;

  @IsString()
  @IsNotEmpty()
  from_attribute_id: string;

  @IsString()
  @IsNotEmpty()
  to_attribute_id: string;
}

export class HolydaysExclusionCheckValidator {
  @IsIn(['holydays_exclusion'])
  @IsString()
  @IsNotEmpty()
  type: 'holydays_exclusion';

  @IsString()
  @IsNotEmpty()
  from_attribute_id: string;

  @IsString()
  @IsNotEmpty()
  to_attribute_id: string;
}

export class WeekendExclusionCheckValidator {
  @IsIn(['weekend_exclusion'])
  @IsString()
  @IsNotEmpty()
  type: 'weekend_exclusion';

  @IsDateString(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  existing_vacations: string[];

  @IsString()
  @IsNotEmpty()
  from_attribute_id: string;

  @IsString()
  @IsNotEmpty()
  to_attribute_id: string;
}

export class WorkingDayCheckValidator {
  @IsIn(['working_day'])
  @IsString()
  @IsNotEmpty()
  type: 'working_day';

  @IsString()
  @IsNotEmpty()
  from_attribute_id: string;

  @IsString()
  @IsNotEmpty()
  to_attribute_id: string;
}

export class MaxDateValidator {
  @IsIn(['max_date'])
  @IsString()
  @IsNotEmpty()
  type: 'max_date';

  @IsDateString()
  @IsNotEmpty()
  max_date: string;

  @IsInt()
  @IsNotEmpty()
  days_count: number;

  @IsBoolean()
  @IsNotEmpty()
  working_days_only: boolean;

  @IsString()
  @IsNotEmpty()
  attribute_id: string;
}

export class MinDateValidator {
  @IsIn(['min_date'])
  @IsString()
  @IsNotEmpty()
  type: 'min_date';

  @IsDateString()
  @IsNotEmpty()
  min_date: string;

  @IsInt()
  @IsNotEmpty()
  days_count: number;

  @IsBoolean()
  @IsNotEmpty()
  working_days_only: boolean;

  @IsString()
  @IsNotEmpty()
  attribute_id: string;
}

export class NumberValidator {
  @IsIn(['number'])
  @IsString()
  @IsNotEmpty()
  type: 'number';

  @IsNumber()
  @IsOptional()
  less_than?: number;

  @IsNumber()
  @IsOptional()
  less_than_or_equal?: number;

  @IsNumber()
  @IsOptional()
  greater_than?: number;

  @IsNumber()
  @IsOptional()
  greater_than_or_equal?: number;

  @IsUUID()
  @IsNotEmpty()
  attribute_id: string;
}

export class ValidatorList {}

export class BatchEventCancelResponse {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  successes: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  errors: string[];
}

export class EventsUkepListResponse {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];
}

export class BatchEventOperationsInfoResponse {
  @IsBoolean()
  @IsNotEmpty()
  optional_attrs_exist: boolean;

  @IsInt()
  @IsNotEmpty()
  total_count_to_accept: number;

  @IsInt()
  @IsNotEmpty()
  total_count_to_cancel: number;

  @IsInt()
  @IsNotEmpty()
  total_count_to_sign_ukep: number;

  @IsInt()
  @IsNotEmpty()
  total_count_to_download: number;

  @IsInt()
  @IsNotEmpty()
  batch_operations_limit: number;

  @IsInt()
  @IsNotEmpty()
  batch_ukep_sign_limit: number;
}

export class EventOperationsResponse {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  successes?: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  errors?: string[];

  @IsUUID()
  @IsOptional()
  download_id?: string;
}

export class Transition {
  @IsIn([
    'upload',
    'upload_batch',
    'accept',
    'ukep_sign',
    'ukep_sign_batch',
    'unep_sign',
    'unep_sign_batch',
    'pep_sign',
    'pep_sign_batch',
    'generate_document_from_template',
    'decline',
    'decline_sign',
    'return',
    'competency_eval',
    'competency_profile',
    'system',
    'system_validate_sign',
    'system_set_vacation_days',
    'system_sync_scheduled_vacations',
    'system_booking_hook',
    'system_booking_approve',
    'system_booking_limits_exceeded_approve',
    'system_booking_trip_create',
    'system_booking_trip_info',
    'system_booking_trip_change',
    'system_booking_trip_ordering',
    'system_booking_trip_limit',
    'system_booking_trip_limit_approved',
    'system_deadline',
    'system_condition_positive',
    'system_condition_negative',
    'system_condition_0',
    'system_condition_1',
    'system_condition_2',
    'system_condition_3',
    'system_condition_4',
    'system_condition_5',
    'system_condition_6',
    'system_condition_7',
    'system_condition_8',
    'system_condition_9',
  ])
  @IsString()
  @IsNotEmpty()
  action_type:
    | 'upload'
    | 'upload_batch'
    | 'accept'
    | 'ukep_sign'
    | 'ukep_sign_batch'
    | 'unep_sign'
    | 'unep_sign_batch'
    | 'pep_sign'
    | 'pep_sign_batch'
    | 'generate_document_from_template'
    | 'decline'
    | 'decline_sign'
    | 'return'
    | 'competency_eval'
    | 'competency_profile'
    | 'system'
    | 'system_validate_sign'
    | 'system_set_vacation_days'
    | 'system_sync_scheduled_vacations'
    | 'system_booking_hook'
    | 'system_booking_approve'
    | 'system_booking_limits_exceeded_approve'
    | 'system_booking_trip_create'
    | 'system_booking_trip_info'
    | 'system_booking_trip_change'
    | 'system_booking_trip_ordering'
    | 'system_booking_trip_limit'
    | 'system_booking_trip_limit_approved'
    | 'system_deadline'
    | 'system_condition_positive'
    | 'system_condition_negative'
    | 'system_condition_0'
    | 'system_condition_1'
    | 'system_condition_2'
    | 'system_condition_3'
    | 'system_condition_4'
    | 'system_condition_5'
    | 'system_condition_6'
    | 'system_condition_7'
    | 'system_condition_8'
    | 'system_condition_9';

  @IsUUID()
  @IsNotEmpty()
  to_node_id: string;
}

export class EventNodeFlowActor {
  @IsString()
  @IsOptional()
  role?: string;

  @IsString()
  @IsOptional()
  fio?: string;
}

export class EventNodeFlow {
  @IsUUID()
  @IsNotEmpty()
  node_id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['finished', 'skipped', 'active', 'canceled', 'inactive'])
  @IsString()
  @IsNotEmpty()
  status: 'finished' | 'skipped' | 'active' | 'canceled' | 'inactive';

  @IsIn([
    'undefined',
    'node_deadline',
    'event_deadline',
    'manual_employee',
    'manual_company',
    'dismiss',
    'workflow_change',
  ])
  @IsString()
  @IsOptional()
  cancel_reason?:
    | 'undefined'
    | 'node_deadline'
    | 'event_deadline'
    | 'manual_employee'
    | 'manual_company'
    | 'dismiss'
    | 'workflow_change';

  @IsString()
  @IsOptional()
  skip_reason?: string;

  @IsDateString()
  @IsOptional()
  activity_date?: string;

  @IsDateString()
  @IsOptional()
  deadline?: string;

  @ValidateNested({ each: true })
  @Type(() => NodeAction)
  @IsArray()
  @IsNotEmpty()
  actions: NodeAction[];

  @ValidateNested()
  @Type(() => CustomState)
  @IsOptional()
  custom_state?: CustomState;

  @ValidateNested()
  @Type(() => EventNodeFlowActor)
  @IsOptional()
  actor?: EventNodeFlowActor;

  @ValidateNested({ each: true })
  @Type(() => Transition)
  @IsArray()
  @IsNotEmpty()
  transitions: Transition[];

  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  documents: string[];

  @IsBoolean()
  @IsNotEmpty()
  is_optional: boolean;
}

export class EventFlow {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsUUID()
  @IsNotEmpty()
  start_node_id: string;

  @IsBoolean()
  @IsNotEmpty()
  in_paper: boolean;

  @ValidateNested({ each: true })
  @Type(() => EventNodeFlow)
  @IsArray()
  @IsNotEmpty()
  nodes: EventNodeFlow[];

  @IsIn(['employee', 'candidate', 'vacation'])
  @IsString()
  @IsNotEmpty()
  category: 'employee' | 'candidate' | 'vacation';
}

export class CreateSimpleEventTypeResponse {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;
}

export class CustomTypes {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  custom_type: string;
}

export class GroupItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class CreateSimpleEventTypeOptionsResponse {
  @ValidateNested({ each: true })
  @Type(() => EventTypeItem)
  @IsArray()
  @IsNotEmpty()
  event_types: EventTypeItem[];

  @ValidateNested({ each: true })
  @Type(() => GroupItem)
  @IsArray()
  @IsNotEmpty()
  signature_groups: GroupItem[];

  @ValidateNested({ each: true })
  @Type(() => CustomTypes)
  @IsArray()
  @IsOptional()
  custom_types?: CustomTypes[];
}

export class CreateSimpleEventTypeCompany {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsBoolean()
  @IsNotEmpty()
  workflow_change_cancels_events: boolean;
}

export class CreateSimpleEventTypeCompaniesResponse {
  @ValidateNested({ each: true })
  @Type(() => CreateSimpleEventTypeCompany)
  @IsArray()
  @IsNotEmpty()
  companies: CreateSimpleEventTypeCompany[];
}

export type EventAvailableFiltersEventTypeGroupOptionsResponse =
  FilterSelectOption[];

export type EventAvailableFiltersDocumentTypeOptionsResponse =
  FilterSelectOption[];

export type EventAvailableFiltersParentEventOptionsResponse =
  FilterSelectOption[];

export type EventAvailableFiltersEventBatchOptionsResponse =
  FilterSelectOption[];

export type EventAvailableFiltersEmployeePositionOptionsResponse =
  FilterSelectOption[];

export class EventAvailableFiltersConsecutiveNumberOptionsResponse {}

export type EventAvailableFiltersEmployeeOptionsResponse = FilterSelectOption[];

export type EventAvailableFiltersAttrOptionsResponse = FilterSelectOption[];

export class AvailableFilters {
  @IsArray()
  @IsNotEmpty()
  filters: (
    | FilterSelect
    | FilterMultiple
    | FilterText
    | FilterSearch
    | FilterMultipleSearch
    | FilterBool
    | FilterTimeRange
  )[];
}

export class EventTypeGroup {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['group'])
  @IsString()
  @IsNotEmpty()
  type: 'group';

  @IsString()
  @IsNotEmpty()
  label: string;

  @ValidateNested({ each: true })
  @Type(() => EventTypeItem)
  @IsArray()
  @IsNotEmpty()
  items: EventTypeItem[];
}

export class CreateEventOptionsEventTypes {
  @IsArray()
  @IsNotEmpty()
  event_types: (EventTypeGroup | EventTypeItem)[];
}

export class CreateEventTypeOptionsCopyAttribute {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsBoolean()
  @IsNotEmpty()
  missing: boolean;
}

export class CreateEventTypeOptionsCopyDocument {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsBoolean()
  @IsNotEmpty()
  missing: boolean;
}

export class ParticipantItem {
  @IsUUID()
  @IsNotEmpty()
  group_id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @ValidateNested()
  @Type(() => CompanyItem)
  @IsOptional()
  company?: CompanyItem;

  @IsString()
  @IsOptional()
  personnel_number?: string;

  @IsString()
  @IsOptional()
  position?: string;
}

export type ParticipantScope = string;

export class AssignableRole {
  @ValidateNested()
  @Type(() => RoleItem)
  @IsNotEmpty()
  role: RoleItem;

  @IsIn(['group', 'company', 'account'])
  @IsString()
  @IsNotEmpty()
  scope: 'group' | 'company' | 'account';

  @ValidateNested()
  @Type(() => ParticipantItem)
  @IsOptional()
  default_group?: ParticipantItem;
}

export class AssignableRoles {
  @ValidateNested({ each: true })
  @Type(() => AssignableRole)
  @IsArray()
  @IsNotEmpty()
  required: AssignableRole[];

  @ValidateNested({ each: true })
  @Type(() => AssignableRole)
  @IsArray()
  @IsNotEmpty()
  optional: AssignableRole[];
}

export class CreateEventTypeOptions {
  @ValidateNested()
  @Type(() => AssignableRoles)
  @IsOptional()
  assignable_roles?: AssignableRoles;

  @ValidateNested({ each: true })
  @Type(() => CreateEventTypeOptionsCopyDocument)
  @IsArray()
  @IsOptional()
  copy_documents?: CreateEventTypeOptionsCopyDocument[];

  @ValidateNested({ each: true })
  @Type(() => CreateEventTypeOptionsCopyAttribute)
  @IsArray()
  @IsOptional()
  copy_attributes?: CreateEventTypeOptionsCopyAttribute[];

  @ValidateNested({ each: true })
  @Type(() => FormAttribute)
  @IsArray()
  @IsOptional()
  form_attributes?: FormAttribute[];

  @IsBoolean()
  @IsOptional()
  document_required?: boolean;
}

export class EventListItemBatch_noticesAccept {
  @IsBoolean()
  @IsNotEmpty()
  optional_attrs_exist: boolean;
}

export class EventListItemBatch_notices {
  @ValidateNested()
  @Type(() => EventListItemBatch_noticesAccept)
  @IsNotEmpty()
  accept: EventListItemBatch_noticesAccept;
}

export class EventListItemBatch_operations {
  @IsBoolean()
  @IsNotEmpty()
  accept: boolean;

  @IsBoolean()
  @IsNotEmpty()
  cancel: boolean;
}

export class CompanySignPermission {
  @IsUUID()
  @IsNotEmpty()
  node_id: string;

  @IsUUID()
  @IsNotEmpty()
  document_id: string;
}

export class Permissions {
  @ValidateNested({ each: true })
  @Type(() => CompanySignPermission)
  @IsArray()
  @IsNotEmpty()
  company_sign: CompanySignPermission[];
}

export class DocumentItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  document_type_name: string;
}

export class EventListItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested({ each: true })
  @Type(() => ActiveNode)
  @IsArray()
  @IsNotEmpty()
  active_nodes: ActiveNode[];

  @IsInt()
  @IsOptional()
  days_on_node?: number;

  @ValidateNested()
  @Type(() => EventTypeItem)
  @IsNotEmpty()
  event_type: EventTypeItem;

  @ValidateNested()
  @Type(() => EventEmployee)
  @IsOptional()
  employee?: EventEmployee;

  @ValidateNested()
  @Type(() => CompanyItem)
  @IsNotEmpty()
  company: CompanyItem;

  @IsDateString()
  @IsNotEmpty()
  created_at: string;

  @ValidateNested({ each: true })
  @Type(() => DocumentItem)
  @IsArray()
  @IsNotEmpty()
  documents: DocumentItem[];

  @IsDateString()
  @IsOptional()
  deadline?: string;

  @ValidateNested()
  @Type(() => Permissions)
  @IsNotEmpty()
  permissions: Permissions;

  @ValidateNested({ each: true })
  @Type(() => Attribute)
  @IsArray()
  @IsNotEmpty()
  attributes: Attribute[];

  @IsString()
  @IsOptional()
  report_status?: string;

  @ValidateNested()
  @Type(() => EventListItemBatch_operations)
  @IsNotEmpty()
  batch_operations: EventListItemBatch_operations;

  @ValidateNested()
  @Type(() => EventListItemBatch_notices)
  @IsNotEmpty()
  batch_notices: EventListItemBatch_notices;

  @IsString()
  @IsOptional()
  consecutive_number?: string;

  @IsBoolean()
  @IsOptional()
  is_urgent?: boolean;
}

export class EmployeeCompetencyEventsResponse {
  @ValidateNested({ each: true })
  @Type(() => EventListItem)
  @IsArray()
  @IsNotEmpty()
  competency_events: EventListItem[];

  @IsInt()
  @IsNotEmpty()
  total_count: number;
}

export class EmployeeCompetency {
  @ValidateNested()
  @Type(() => CompetencyItem)
  @IsNotEmpty()
  competency: CompetencyItem;

  @ValidateNested({ each: true })
  @Type(() => CompetencyScaleValue)
  @IsArray()
  @IsNotEmpty()
  scale_values: CompetencyScaleValue[];

  @ValidateNested()
  @Type(() => CompetencyScaleValue)
  @IsOptional()
  required_level?: CompetencyScaleValue;

  @ValidateNested()
  @Type(() => CompetencyScaleValue)
  @IsOptional()
  actual_level?: CompetencyScaleValue;
}

export class EmployeeCompetencyGroup {
  @IsString()
  @IsNotEmpty()
  name: string;

  @ValidateNested({ each: true })
  @Type(() => EmployeeCompetency)
  @IsArray()
  @IsNotEmpty()
  competencies: EmployeeCompetency[];
}

export class EmployeeCompetencyResponse {
  @ValidateNested({ each: true })
  @Type(() => EventListItem)
  @IsArray()
  @IsNotEmpty()
  competency_events: EventListItem[];

  @ValidateNested({ each: true })
  @Type(() => EmployeeCompetencyGroup)
  @IsArray()
  @IsNotEmpty()
  competency_groups: EmployeeCompetencyGroup[];
}

export class EmployeeCreatableEventTypes {
  @IsArray()
  @IsNotEmpty()
  event_types: (EventTypeGroup | EventTypeItem)[];
}

export class EmployeesList {
  @ValidateNested({ each: true })
  @Type(() => EmployeeItem)
  @IsArray()
  @IsNotEmpty()
  employees: EmployeeItem[];
}

export class CreateEventTypeParticipantOptions {
  @ValidateNested({ each: true })
  @Type(() => ParticipantItem)
  @IsArray()
  @IsNotEmpty()
  options: ParticipantItem[];
}

export class CreateEventCompanyItem {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsString()
  @IsNotEmpty()
  company_name: string;
}

export class CreateEventCompanyOptionsList {
  @ValidateNested({ each: true })
  @Type(() => CreateEventCompanyItem)
  @IsArray()
  @IsNotEmpty()
  items: CreateEventCompanyItem[];
}

export class CreateEventCommonOptionsResponse {
  @IsBoolean()
  @IsNotEmpty()
  has_batch: boolean;

  @IsBoolean()
  @IsNotEmpty()
  creator_for_others: boolean;
}

export class EventListToPaperResponse {
  @ValidateNested({ each: true })
  @Type(() => EventListItem)
  @IsArray()
  @IsNotEmpty()
  events: EventListItem[];

  @IsInt()
  @IsNotEmpty()
  total_count: number;
}

export class EventDocumentCountResponse {
  @IsInt()
  @IsNotEmpty()
  total_count: number;
}

export class EventPdfResponse {
  @IsUUID()
  @IsNotEmpty()
  download_id: string;
}

export class EventXlsxResponse {
  @IsUUID()
  @IsNotEmpty()
  download_id: string;
}

export class EventListResponse {
  @ValidateNested({ each: true })
  @Type(() => EventListItem)
  @IsArray()
  @IsNotEmpty()
  events: EventListItem[];

  @IsInt()
  @IsNotEmpty()
  total_count: number;

  @IsInt()
  @IsOptional()
  total_count_my?: number;

  @IsInt()
  @IsOptional()
  total_count_my_group?: number;

  @IsInt()
  @IsOptional()
  total_count_all?: number;

  @IsInt()
  @IsOptional()
  finished_count?: number;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  parent_event_tags?: string[];
}

export class CandidateEventListResponse {
  @ValidateNested({ each: true })
  @Type(() => EventListItem)
  @IsArray()
  @IsNotEmpty()
  events: EventListItem[];

  @IsInt()
  @IsNotEmpty()
  total_count: number;
}

export class EventBatchEmployeeCountResponse {
  @IsInt()
  @IsNotEmpty()
  total: number;

  @IsInt()
  @IsNotEmpty()
  allowed: number;
}

export class EventBatchEmployee {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  personnel_number: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsBoolean()
  @IsNotEmpty()
  enabled: boolean;

  @IsString()
  @IsOptional()
  position?: string;

  @IsString()
  @IsOptional()
  unit_name?: string;
}

export class EventBatchEmployeesResponse {
  @ValidateNested({ each: true })
  @Type(() => EventBatchEmployee)
  @IsArray()
  @IsNotEmpty()
  employees: EventBatchEmployee[];
}

export class CreateEventBatchResponse {
  @IsUUID()
  @IsNotEmpty()
  event_batch_id: string;
}

export class EventBatchItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class EventBatchListResponse {
  @ValidateNested({ each: true })
  @Type(() => EventBatchItem)
  @IsArray()
  @IsNotEmpty()
  event_batches: EventBatchItem[];
}

export class CreateEventResponse {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class CancelReasonItem {
  @IsInt()
  @IsNotEmpty()
  id: number;

  @IsString()
  @IsNotEmpty()
  description: string;

  @IsBoolean()
  @IsNotEmpty()
  batch_cancel_enabled: boolean;
}

export class CancelReasonListResponse {
  @ValidateNested({ each: true })
  @Type(() => CancelReasonItem)
  @IsArray()
  @IsNotEmpty()
  reasons: CancelReasonItem[];
}

export class ScheduleEventsListFilters {
  @IsIn(['my', 'subordinates', 'all', 'favorites'])
  @IsString()
  @IsOptional()
  actor?: 'my' | 'subordinates' | 'all' | 'favorites';

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  employee_ids?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  unit_ids?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  status?: ('new' | 'accept' | 'finished' | 'canceled' | 'onec_sync')[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  active_participant?: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  event_ids?: string[];
}

export class ScheduleEventsXlsxRequest {
  @ValidateNested()
  @Type(() => ScheduleEventsListFilters)
  @IsOptional()
  filters?: ScheduleEventsListFilters;
}

export class Pagination {
  @IsString()
  @IsOptional()
  sort_by?: string;

  @IsString()
  @IsOptional()
  sort_order?: string;

  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class ScheduleEventsListRequest {
  @ValidateNested()
  @Type(() => Pagination)
  @IsOptional()
  pagination?: Pagination;

  @ValidateNested()
  @Type(() => ScheduleEventsListFilters)
  @IsOptional()
  filters?: ScheduleEventsListFilters;

  @IsUUID()
  @IsOptional()
  last_event_id?: string;
}

export class EventAttributeCacheFileRequest {
  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @Type(() => String)
  @IsNotEmpty()
  file: File;
}

export class GetEventFileHashesRequestHashes {}

export class GetEventFileHashesRequest {
  @ValidateNested({ each: true })
  @Type(() => GetEventFileHashesRequestHashes)
  @IsArray()
  @IsNotEmpty()
  hashes: GetEventFileHashesRequestHashes[];
}

export class CreateEventBatchDocumentZipRequest {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  event_ids: string[];

  @IsBoolean()
  @IsNotEmpty()
  with_file_attributes: boolean;
}

export class CompetencyProfileRequest {
  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  competency_values: string[];

  @IsNotEmpty()
  attributes: object;
}

export class CompetencyEvalRequest {
  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  competency_values: string[];

  @IsNotEmpty()
  attributes: object;
}

export class BatchEventReturn {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class BatchReturnRequest {
  @ValidateNested({ each: true })
  @Type(() => BatchEventReturn)
  @IsArray()
  @IsNotEmpty()
  events: BatchEventReturn[];

  @IsString()
  @IsOptional()
  comment?: string;
}

export class BatchEventAccept {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class BatchAcceptRequest {
  @ValidateNested({ each: true })
  @Type(() => BatchEventAccept)
  @IsArray()
  @IsNotEmpty()
  events: BatchEventAccept[];
}

export class PepSignEvent {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;
}

export class PepBatchSignRequest {
  @ValidateNested({ each: true })
  @Type(() => PepSignEvent)
  @IsArray()
  @IsNotEmpty()
  events: PepSignEvent[];
}

export class PepSignRequest {
  @IsNotEmpty()
  attributes: object;
}

export class UkepSignDocument {
  @IsString()
  @IsNotEmpty()
  document_hash: string;

  @IsString()
  @IsNotEmpty()
  signature: string;
}

export class UkepSignEvent {
  @IsUUID()
  @IsNotEmpty()
  event_id: string;

  @ValidateNested({ each: true })
  @Type(() => UkepSignDocument)
  @IsArray()
  @IsNotEmpty()
  documents: UkepSignDocument[];
}

export class UkepBatchSignRequest {
  @ValidateNested({ each: true })
  @Type(() => UkepSignEvent)
  @IsArray()
  @IsNotEmpty()
  events: UkepSignEvent[];
}

export class UkepSignRequest {
  @IsNotEmpty()
  attributes: object;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  hash: string;
}

export class UnepSignRequest {
  @IsNotEmpty()
  attributes: object;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  code?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsOptional()
  signatures?: object;
}

export class GenerateDocumentRequest {
  @IsNotEmpty()
  attributes: object;

  @IsDateString()
  @IsOptional()
  document_date?: string;

  @IsString()
  @IsOptional()
  document_number?: string;
}

export class DeclineSignRequest {
  @IsString()
  @IsNotEmpty()
  comment: string;

  @IsNotEmpty()
  attributes: object;
}

export class DeclineRequest {
  @IsNotEmpty()
  attributes: object;
}

export class AcceptRequest {
  @IsNotEmpty()
  attributes: object;
}

export class ReturnRequest {
  @IsString()
  @IsNotEmpty()
  comment: string;
}

export class UploadBatchDocument {
  @IsUUID()
  @IsNotEmpty()
  node_id: string;

  @IsDateString()
  @IsOptional()
  document_date?: string;

  @IsString()
  @IsOptional()
  document_number?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @Type(() => String)
  @IsNotEmpty()
  document: File;
}

export class UploadBatchRequest {
  @ValidateNested({ each: true })
  @Type(() => UploadBatchDocument)
  @IsArray()
  @IsNotEmpty()
  documents: UploadBatchDocument[];
}

export class UploadRequest {
  @IsNotEmpty()
  attributes: object;

  @IsDateString()
  @IsOptional()
  document_date?: string;

  @IsString()
  @IsOptional()
  document_number?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @Type(() => String)
  @IsNotEmpty()
  document: File;
}

export class ToPaperRequest {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  events: string[];
}

export class CancelEventRequest {
  @IsInt()
  @IsNotEmpty()
  reason_id: number;

  @IsString()
  @IsOptional()
  comment?: string;
}

export class BatchCancelRequest {}

export class EventListRequestFilters {
  @IsString()
  @IsOptional()
  actor?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  event_type_groups?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  company?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  unit_ids?: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  employee_ids?: string[];

  @IsString()
  @IsOptional()
  personnel_number?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  employee_positions?: string[];

  @ValidateNested()
  @Type(() => TimeRange)
  @IsOptional()
  finished_at?: TimeRange;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  status?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  cancel_reason_type?: (
    | 'deadline'
    | 'manual_employee'
    | 'on_employee_before'
    | 'on_employee_after'
    | 'manual_company'
    | 'workflow_change'
    | 'dismiss'
  )[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  event_batch_ids?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  side?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  segments?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  document_type?: string[];

  @ValidateNested()
  @Type(() => TimeRange)
  @IsOptional()
  deadline?: TimeRange;

  @IsUUID()
  @IsOptional()
  parent_event_id?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  tags?: string[];

  @IsUUID()
  @IsOptional()
  collection_id?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  event_ids?: string[];

  @IsString()
  @IsOptional()
  consecutive_number?: string;

  @IsString()
  @IsOptional()
  attr_absence_type?: string;

  @ValidateNested()
  @Type(() => TimeRange)
  @IsOptional()
  attr_absence_start_date?: TimeRange;

  @ValidateNested()
  @Type(() => TimeRange)
  @IsOptional()
  attr_absence_end_date?: TimeRange;

  @IsString()
  @IsOptional()
  attr_vacation_compensation?: string;

  @ValidateNested()
  @Type(() => TimeRange)
  @IsOptional()
  attr_goals_dates?: TimeRange;

  @IsString()
  @IsOptional()
  absences_report_status?: string;
}

export class EventsUkepListRequest {
  @ValidateNested()
  @Type(() => EventListRequestFilters)
  @IsOptional()
  filters?: EventListRequestFilters;
}

export class EventListRequest {
  @ValidateNested()
  @Type(() => Pagination)
  @IsOptional()
  pagination?: Pagination;

  @IsBoolean()
  @IsOptional()
  urgent_first?: boolean;

  @ValidateNested()
  @Type(() => EventListRequestFilters)
  @IsOptional()
  filters?: EventListRequestFilters;
}

export class EventOperationsRequest {
  @ValidateNested()
  @Type(() => EventListRequestFilters)
  @IsOptional()
  filters?: EventListRequestFilters;

  @IsIn(['batch_accept', 'batch_cancel', 'batch_download'])
  @IsString()
  @IsNotEmpty()
  operation: 'batch_accept' | 'batch_cancel' | 'batch_download';

  @ValidateNested()
  @Type(() => CancelEventRequest)
  @IsOptional()
  cancel_payload?: CancelEventRequest;
}

export class CreateSimpleEventTypeRequest {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['company', 'employee', 'company_employee', 'employee_company'])
  @IsString()
  @IsNotEmpty()
  pattern: 'company' | 'employee' | 'company_employee' | 'employee_company';

  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  documents: string[];

  @IsUUID()
  @IsOptional()
  signature_group_id?: string;

  @IsUUID()
  @IsOptional()
  custom_type?: string;

  @IsUUID()
  @IsOptional()
  person_accept?: string;

  @IsUUID()
  @IsNotEmpty()
  person_upload: string;
}

export class EmployeeCompetencyEventsRequest {
  @ValidateNested()
  @Type(() => Pagination)
  @IsOptional()
  pagination?: Pagination;
}

export class EventListExportRequest {
  @IsIn(['xlsx'])
  @IsString()
  @IsNotEmpty()
  format: 'xlsx';

  @ValidateNested()
  @Type(() => EventListRequestFilters)
  @IsOptional()
  filters?: EventListRequestFilters;
}

export class EventListToPaperRequest {
  @ValidateNested()
  @Type(() => Pagination)
  @IsOptional()
  pagination?: Pagination;
}

export class EventDocumentCountRequest {
  @ValidateNested()
  @Type(() => EventListRequestFilters)
  @IsOptional()
  filters?: EventListRequestFilters;
}

export class EventPdfRequest {
  @ValidateNested()
  @Type(() => EventListRequestFilters)
  @IsOptional()
  filters?: EventListRequestFilters;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  event_ids?: string[];
}

export class EventXlsxRequest {
  @ValidateNested()
  @Type(() => EventListRequestFilters)
  @IsOptional()
  filters?: EventListRequestFilters;
}

export class CandidateEventsFilter {
  @IsString()
  @IsOptional()
  actor?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  company?: string[];

  @IsString()
  @IsOptional()
  employee_id?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  status?: ('upload' | 'sign' | 'accept' | 'finished' | 'canceled')[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  side?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  segments?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  document_type?: string[];

  @ValidateNested()
  @Type(() => TimeRange)
  @IsOptional()
  deadline?: TimeRange;
}

export class CandidateEventListRequest {
  @ValidateNested()
  @Type(() => Pagination)
  @IsOptional()
  pagination?: Pagination;

  @IsBoolean()
  @IsOptional()
  urgent_first?: boolean;

  @ValidateNested()
  @Type(() => CandidateEventsFilter)
  @IsOptional()
  filters?: CandidateEventsFilter;
}

export class CreateEventBatchRequest {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;

  @IsBoolean()
  @IsOptional()
  all_employees?: boolean;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  unit_ids?: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  employee_ids?: string[];

  @IsUUID()
  @IsOptional()
  event_batch_id?: string;

  @IsString()
  @IsOptional()
  event_batch_name?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @Type(() => String)
  @IsOptional()
  document?: File;

  @IsDateString()
  @IsOptional()
  document_date?: string;

  @IsString()
  @IsOptional()
  document_number?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  assigned_roles?: string[];

  @IsUUID()
  @IsOptional()
  parent_event_id?: string;

  @IsOptional()
  attributes?: object;
}

export class AssignedRole {
  @IsUUID()
  @IsNotEmpty()
  role_id: string;

  @IsUUID()
  @IsNotEmpty()
  group_id: string;
}

export class CreateEventRequest {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;

  @IsUUID()
  @IsOptional()
  employee_id?: string;

  @ValidateNested({ each: true })
  @Type(() => AssignedRole)
  @IsArray()
  @IsOptional()
  assigned_roles?: AssignedRole[];

  @IsUUID()
  @IsOptional()
  parent_event_id?: string;

  @IsOptional()
  attributes?: object;
}

@injectable()
export class EventsService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async getEventEmployeeAbsenceList({
    params,
  }: {
    params: GetEventEmployeeAbsenceListParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        GetEventEmployeeAbsenceListParams,
      ),
    ]);

    let requestPath = '/event/{event_id}/absences';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetEventEmployeeAbsenceListParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      AbsenceListResponse,
    );

    return response;
  }

  async getEventEmployees({
    query = {},
    params,
  }: {
    query?: GetEventEmployeesQuery;
    params: GetEventEmployeesParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetEventEmployeesQuery),
      this.validator.validateOrReject(params, GetEventEmployeesParams),
    ]);

    let requestPath = '/event/{event_id}/employees';

    const paramsKeys = Object.keys(params) as (keyof GetEventEmployeesParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetEventEmployeesResponse,
    );

    return response;
  }

  async getEventStaffPositionEmployees({
    query = {},
    params,
  }: {
    query?: GetEventStaffPositionEmployeesQuery;
    params: GetEventStaffPositionEmployeesParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetEventStaffPositionEmployeesQuery,
      ),
      this.validator.validateOrReject(
        params,
        GetEventStaffPositionEmployeesParams,
      ),
    ]);

    let requestPath = '/event/{event_id}/staff_position_employees';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetEventStaffPositionEmployeesParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetEventStaffPositionEmployeesResponse,
    );

    return response;
  }

  async getEventCompetencyOptions({
    query,
    params,
  }: {
    query: GetEventCompetencyOptionsQuery;
    params: GetEventCompetencyOptionsParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetEventCompetencyOptionsQuery),
      this.validator.validateOrReject(params, GetEventCompetencyOptionsParams),
    ]);

    let requestPath = '/event/{event_id}/competency_options';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetEventCompetencyOptionsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetEventCompetencyOptionsResponse,
    );

    return response;
  }

  async getEventAttributeOptions({
    query = {},
    params,
  }: {
    query?: GetEventAttributeOptionsQuery;
    params: GetEventAttributeOptionsParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetEventAttributeOptionsQuery),
      this.validator.validateOrReject(params, GetEventAttributeOptionsParams),
    ]);

    let requestPath = '/event/{event_id}/attribute/{attribute_id}/options';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetEventAttributeOptionsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      EventCatalogAttributes,
    );

    return response;
  }

  async getVacationScheduleEvent({
    params,
  }: {
    params: GetVacationScheduleEventParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetVacationScheduleEventParams),
    ]);

    let requestPath = '/vacation/event/{event_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetVacationScheduleEventParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(result, ScheduleEvent);

    return response;
  }

  async getScheduleConsecutiveNumberFilterOptions({
    query,
  }: {
    query: GetScheduleConsecutiveNumberFilterOptionsQuery;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetScheduleConsecutiveNumberFilterOptionsQuery,
      ),
    ]);

    let requestPath =
      '/vacation/schedule/available_filters/consecutive_number_options';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      VacationScheduleAvailableFiltersConsecutiveNumberOptionsResponse,
    );

    return response;
  }

  async getScheduleEventEmployeeFilterOptions({
    query,
  }: {
    query: GetScheduleEventEmployeeFilterOptionsQuery;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetScheduleEventEmployeeFilterOptionsQuery,
      ),
    ]);

    let requestPath = '/vacation/schedule/available_filters/employee_options';

    const result = await this.http.get(requestPath, query, { withSide: true });

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getAvailableScheduleEventFilters({
    params,
  }: {
    params: GetAvailableScheduleEventFiltersParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        GetAvailableScheduleEventFiltersParams,
      ),
    ]);

    let requestPath = '/vacation/schedule/{schedule_id}/available_filters';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetAvailableScheduleEventFiltersParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      FiltersResponse,
    );

    return response;
  }

  async getScheduleFavoritesEvents({
    query = {},
    params,
  }: {
    query?: GetScheduleFavoritesEventsQuery;
    params: GetScheduleFavoritesEventsParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetScheduleFavoritesEventsQuery),
      this.validator.validateOrReject(params, GetScheduleFavoritesEventsParams),
    ]);

    let requestPath = '/vacation/schedule/{schedule_id}/favorites/list';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetScheduleFavoritesEventsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      ScheduleFavoriteEventsListResponse,
    );

    return response;
  }

  async getScheduleEventsXlsx({
    params,
    body = {},
  }: {
    params: GetScheduleEventsXlsxParams;
    body?: ScheduleEventsXlsxRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetScheduleEventsXlsxParams),
      this.validator.validateOrReject(body, ScheduleEventsXlsxRequest),
    ]);

    let requestPath = '/vacation/schedule/{schedule_id}/xlsx';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetScheduleEventsXlsxParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      ScheduleEventsXlsxResponse,
    );

    return response;
  }

  async getScheduleEvents({
    params,
    body = {},
  }: {
    params: GetScheduleEventsParams;
    body?: ScheduleEventsListRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetScheduleEventsParams),
      this.validator.validateOrReject(body, ScheduleEventsListRequest),
    ]);

    let requestPath = '/vacation/schedule/{schedule_id}/list';

    const paramsKeys = Object.keys(params) as (keyof GetScheduleEventsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      ScheduleEventsListResponse,
    );

    return response;
  }

  async eventAttributeCacheFile({
    params,
    body,
  }: {
    params: EventAttributeCacheFileParams;
    body: EventAttributeCacheFileRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, EventAttributeCacheFileParams),
      this.validator.validateOrReject(body, EventAttributeCacheFileRequest),
    ]);

    let requestPath = '/event/{event_id}/attribute/{attribute_id}/cache_file';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof EventAttributeCacheFileParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      EventAttributeCacheFileResponse,
    );

    return response;
  }

  async getEventAttributeFile({
    query = {},
    params,
  }: {
    query?: GetEventAttributeFileQuery;
    params: GetEventAttributeFileParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetEventAttributeFileQuery),
      this.validator.validateOrReject(params, GetEventAttributeFileParams),
    ]);

    let requestPath = '/event/{event_id}/attribute/{attribute_id}/file';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetEventAttributeFileParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.getFile(
      requestPath,
      {
        withAuth: true,
        withSide: true,
        isJson: true,
      },
      query,
    );

    return result;
  }

  async getFileHashes({ body }: { body: GetEventFileHashesRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, GetEventFileHashesRequest),
    ]);

    let requestPath = '/event/batch/document/hash';

    const result = await this.http.post(requestPath, body, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetEventFileHashesResponse,
    );

    return response;
  }

  async markDocumentAsRead({ params }: { params: MarkDocumentAsReadParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, MarkDocumentAsReadParams),
    ]);

    let requestPath = '/event/{event_id}/document/{document_id}/mark_read';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof MarkDocumentAsReadParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, {}, { withSide: true });

    return result;
  }

  async getEventDocumentFileHash({
    params,
  }: {
    params: GetEventDocumentFileHashParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetEventDocumentFileHashParams),
    ]);

    let requestPath = '/event/{event_id}/document/{document_id}/file/hash';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetEventDocumentFileHashParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetEventDocumentFileHashResponse,
    );

    return response;
  }

  async eventFlowPdf({ params }: { params: EventFlowPdfParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, EventFlowPdfParams),
    ]);

    let requestPath = '/event/{event_id}/flow_pdf';

    const paramsKeys = Object.keys(params) as (keyof EventFlowPdfParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.getFile(requestPath, {
      withAuth: true,
      withSide: false,
      isJson: true,
    });

    return result;
  }

  async createEventBatchDocumentZip({
    body,
  }: {
    body: CreateEventBatchDocumentZipRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(body, CreateEventBatchDocumentZipRequest),
    ]);

    let requestPath = '/event/batch/document/zip';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreateEventBatchDocumentZipResponse,
    );

    return response;
  }

  async requestEventDocumentDocxFile({
    params,
  }: {
    params: RequestEventDocumentDocxFileParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        RequestEventDocumentDocxFileParams,
      ),
    ]);

    let requestPath = '/event/{event_id}/document/docx';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof RequestEventDocumentDocxFileParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      RequestEventDocumentDocxResponse,
    );

    return response;
  }

  async getEventDocumentZip({ params }: { params: GetEventDocumentZipParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetEventDocumentZipParams),
    ]);

    let requestPath = '/event/{event_id}/document/{document_id}/zip';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetEventDocumentZipParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.getFile(requestPath, {
      withAuth: true,
      withSide: true,
      isJson: true,
    });

    return result;
  }

  async getEventDocumentFileWithStamp({
    params,
  }: {
    params: GetEventDocumentFileWithStampParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        GetEventDocumentFileWithStampParams,
      ),
    ]);

    let requestPath =
      '/event/{event_id}/document/{document_id}/file_with_stamp';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetEventDocumentFileWithStampParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.getFile(requestPath, {
      withAuth: true,
      withSide: true,
      isJson: true,
    });

    return result;
  }

  async getEventDocumentFile({
    params,
  }: {
    params: GetEventDocumentFileParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetEventDocumentFileParams),
    ]);

    let requestPath = '/event/{event_id}/document/{document_id}/file';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetEventDocumentFileParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.getFile(requestPath, {
      withAuth: true,
      withSide: true,
      isJson: true,
    });

    return result;
  }

  async doCompetencyProfile({
    params,
    body,
  }: {
    params: DoCompetencyProfileParams;
    body: CompetencyProfileRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DoCompetencyProfileParams),
      this.validator.validateOrReject(body, CompetencyProfileRequest),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/competency_profile';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof DoCompetencyProfileParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    return result;
  }

  async doCompetencyEval({
    params,
    body,
  }: {
    params: DoCompetencyEvalParams;
    body: CompetencyEvalRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DoCompetencyEvalParams),
      this.validator.validateOrReject(body, CompetencyEvalRequest),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/competency_eval';

    const paramsKeys = Object.keys(params) as (keyof DoCompetencyEvalParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    return result;
  }

  async doBatchReturn({ body }: { body: BatchReturnRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, BatchReturnRequest),
    ]);

    let requestPath = '/event/batch/return';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      BatchEventOperationResponse,
    );

    return response;
  }

  async doBatchAccept({ body }: { body: BatchAcceptRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, BatchAcceptRequest),
    ]);

    let requestPath = '/event/batch/accept';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      BatchEventAcceptResponse,
    );

    return response;
  }

  async doBatchPepSign({ body }: { body: PepBatchSignRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, PepBatchSignRequest),
    ]);

    let requestPath = '/event/batch/pep_sign';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      BatchEventOperationResponse,
    );

    return response;
  }

  async doPepSign({
    params,
    body,
  }: {
    params: DoPepSignParams;
    body: PepSignRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DoPepSignParams),
      this.validator.validateOrReject(body, PepSignRequest),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/pep_sign';

    const paramsKeys = Object.keys(params) as (keyof DoPepSignParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    return result;
  }

  async doBatchUkepSign({ body }: { body: UkepBatchSignRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, UkepBatchSignRequest),
    ]);

    let requestPath = '/event/batch/ukep_sign';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      BatchEventOperationResponse,
    );

    return response;
  }

  async doUkepSign({
    params,
    body,
  }: {
    params: DoUkepSignParams;
    body: UkepSignRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DoUkepSignParams),
      this.validator.validateOrReject(body, UkepSignRequest),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/ukep_sign';

    const paramsKeys = Object.keys(params) as (keyof DoUkepSignParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    return result;
  }

  async requestUnepSignBatchCode({
    params,
  }: {
    params: RequestUnepSignBatchCodeParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, RequestUnepSignBatchCodeParams),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/unep_sign_batch/code';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof RequestUnepSignBatchCodeParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, {}, { withSide: true });

    return result;
  }

  async doUnepSignBatch({
    params,
    body,
  }: {
    params: DoUnepSignBatchParams;
    body: UnepSignRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DoUnepSignBatchParams),
      this.validator.validateOrReject(body, UnepSignRequest),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/unep_sign_batch';

    const paramsKeys = Object.keys(params) as (keyof DoUnepSignBatchParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    return result;
  }

  async requestUnepSignCode({ params }: { params: RequestUnepSignCodeParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, RequestUnepSignCodeParams),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/unep_sign/code';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof RequestUnepSignCodeParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, {}, { withSide: true });

    return result;
  }

  async doUnepSign({
    params,
    body,
  }: {
    params: DoUnepSignParams;
    body: UnepSignRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DoUnepSignParams),
      this.validator.validateOrReject(body, UnepSignRequest),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/unep_sign';

    const paramsKeys = Object.keys(params) as (keyof DoUnepSignParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    return result;
  }

  async documentGenerateFromTemplate({
    params,
    body,
  }: {
    params: DocumentGenerateFromTemplateParams;
    body: GenerateDocumentRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        DocumentGenerateFromTemplateParams,
      ),
      this.validator.validateOrReject(body, GenerateDocumentRequest),
    ]);

    let requestPath =
      '/event/{event_id}/{node_id}/generate_document_from_template';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof DocumentGenerateFromTemplateParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    return result;
  }

  async documentGeneratePreviewFromTemplate({
    params,
    body,
  }: {
    params: DocumentGeneratePreviewFromTemplateParams;
    body: GenerateDocumentRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        DocumentGeneratePreviewFromTemplateParams,
      ),
      this.validator.validateOrReject(body, GenerateDocumentRequest),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/generate_document_preview';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof DocumentGeneratePreviewFromTemplateParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.generateFile(requestPath, body, {
      method: 'put',
      withSide: true,
      isJson: false,
    });

    return result;
  }

  async doDeclineSign({
    params,
    body,
  }: {
    params: DoDeclineSignParams;
    body: DeclineSignRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DoDeclineSignParams),
      this.validator.validateOrReject(body, DeclineSignRequest),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/decline_sign';

    const paramsKeys = Object.keys(params) as (keyof DoDeclineSignParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    return result;
  }

  async doDecline({
    params,
    body,
  }: {
    params: DoDeclineParams;
    body: DeclineRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DoDeclineParams),
      this.validator.validateOrReject(body, DeclineRequest),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/decline';

    const paramsKeys = Object.keys(params) as (keyof DoDeclineParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    return result;
  }

  async doRetry({ params }: { params: DoRetryParams }) {
    await Promise.all([this.validator.validateOrReject(params, DoRetryParams)]);

    let requestPath = '/event/{event_id}/{node_id}/{action_type}/retry';

    const paramsKeys = Object.keys(params) as (keyof DoRetryParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, {}, { withSide: true });

    return result;
  }

  async doAccept({
    params,
    body,
  }: {
    params: DoAcceptParams;
    body: AcceptRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DoAcceptParams),
      this.validator.validateOrReject(body, AcceptRequest),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/accept';

    const paramsKeys = Object.keys(params) as (keyof DoAcceptParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    return result;
  }

  async doReturn({
    params,
    body,
  }: {
    params: DoReturnParams;
    body: ReturnRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DoReturnParams),
      this.validator.validateOrReject(body, ReturnRequest),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/return';

    const paramsKeys = Object.keys(params) as (keyof DoReturnParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async doUploadBatch({
    params,
    body,
  }: {
    params: DoUploadBatchParams;
    body: UploadBatchRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DoUploadBatchParams),
      this.validator.validateOrReject(body, UploadBatchRequest),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/upload_batch';

    const paramsKeys = Object.keys(params) as (keyof DoUploadBatchParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    return result;
  }

  async doUpload({
    params,
    body,
  }: {
    params: DoUploadParams;
    body: UploadRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DoUploadParams),
      this.validator.validateOrReject(body, UploadRequest),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/upload';

    const paramsKeys = Object.keys(params) as (keyof DoUploadParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    return result;
  }

  async eventNodeValidators({ params }: { params: EventNodeValidatorsParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, EventNodeValidatorsParams),
    ]);

    let requestPath = '/event/{event_id}/{node_id}/validators';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof EventNodeValidatorsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(result, ValidatorList);

    return response;
  }

  async eventToPaper({ body }: { body: ToPaperRequest }) {
    await Promise.all([this.validator.validateOrReject(body, ToPaperRequest)]);

    let requestPath = '/event/batch/to_paper';

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async restoreEvent({ params }: { params: RestoreEventParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, RestoreEventParams),
    ]);

    let requestPath = '/event/{event_id}/restore';

    const paramsKeys = Object.keys(params) as (keyof RestoreEventParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, {}, { withSide: true });

    return result;
  }

  async cancelEvent({
    params,
    body,
  }: {
    params: CancelEventParams;
    body: CancelEventRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, CancelEventParams),
      this.validator.validateOrReject(body, CancelEventRequest),
    ]);

    let requestPath = '/event/{event_id}/cancel';

    const paramsKeys = Object.keys(params) as (keyof CancelEventParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async doBatchCancel({
    body = {},
  }: {
    body?: BatchCancelRequest;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(body, BatchCancelRequest),
    ]);

    let requestPath = '/event/batch/cancel';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      BatchEventCancelResponse,
    );

    return response;
  }

  async ukepListEvents({
    body = {},
  }: {
    body?: EventsUkepListRequest;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(body, EventsUkepListRequest),
    ]);

    let requestPath = '/event/to_sign_ukep/ids';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      EventsUkepListResponse,
    );

    return response;
  }

  async getBatchEventOperationsInfo({
    body = {},
  }: {
    body?: EventListRequest;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(body, EventListRequest),
    ]);

    let requestPath = '/event/batch_operations/info';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      BatchEventOperationsInfoResponse,
    );

    return response;
  }

  async operationEvents({ body }: { body: EventOperationsRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, EventOperationsRequest),
    ]);

    let requestPath = '/event/operations';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      EventOperationsResponse,
    );

    return response;
  }

  async getEvent({ params }: { params: GetEventParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetEventParams),
    ]);

    let requestPath = '/event/{event_id}';

    const paramsKeys = Object.keys(params) as (keyof GetEventParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(result, Event);

    return response;
  }

  async getEventFlow({ params }: { params: GetEventFlowParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetEventFlowParams),
    ]);

    let requestPath = '/event/{event_id}/flow';

    const paramsKeys = Object.keys(params) as (keyof GetEventFlowParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(result, EventFlow);

    return response;
  }

  async getBookingRedirect({
    query,
    params,
  }: {
    query: GetBookingRedirectQuery;
    params: GetBookingRedirectParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetBookingRedirectQuery),
      this.validator.validateOrReject(params, GetBookingRedirectParams),
    ]);

    let requestPath = '/event/{event_id}/booking/corteos/trip';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetBookingRedirectParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, { withAuth: false });

    return result;
  }

  async createSimpleEventType({
    body,
  }: {
    body: CreateSimpleEventTypeRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(body, CreateSimpleEventTypeRequest),
    ]);

    let requestPath = '/event_type/simple';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreateSimpleEventTypeResponse,
    );

    return response;
  }

  async getCreateSimpleEventTypeOptions({
    query,
  }: {
    query: GetCreateSimpleEventTypeOptionsQuery;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetCreateSimpleEventTypeOptionsQuery,
      ),
    ]);

    let requestPath = '/event_type/simple/create_options';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreateSimpleEventTypeOptionsResponse,
    );

    return response;
  }

  async getCreateSimpleEventTypeCompanies() {
    let requestPath = '/event_type/simple/create_companies';

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreateSimpleEventTypeCompaniesResponse,
    );

    return response;
  }

  async getEventTypeGroupFilterOptions({
    query = {},
  }: {
    query?: GetEventTypeGroupFilterOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetEventTypeGroupFilterOptionsQuery,
      ),
    ]);

    let requestPath = '/event/available_filters/event_type_group_options';

    const result = await this.http.get(requestPath, query, { withSide: true });

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getDocumentTypeFilterOptions({
    query = {},
  }: {
    query?: GetDocumentTypeFilterOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetDocumentTypeFilterOptionsQuery),
    ]);

    let requestPath = '/event/available_filters/document_type_options';

    const result = await this.http.get(requestPath, query, {});

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getParentEventFilterOptions({
    query = {},
  }: {
    query?: GetParentEventFilterOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetParentEventFilterOptionsQuery),
    ]);

    let requestPath = '/event/available_filters/parent_event_options';

    const result = await this.http.get(requestPath, query, { withSide: true });

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getEventBatchFilterOptions({
    query = {},
  }: {
    query?: GetEventBatchFilterOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetEventBatchFilterOptionsQuery),
    ]);

    let requestPath = '/event/available_filters/event_batch_options';

    const result = await this.http.get(requestPath, query, {});

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getEmployeePositionFilterOptions({
    query = {},
  }: {
    query?: GetEmployeePositionFilterOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetEmployeePositionFilterOptionsQuery,
      ),
    ]);

    let requestPath = '/event/available_filters/employee_position_options';

    const result = await this.http.get(requestPath, query, { withSide: true });

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getConsecutiveNumberFilterOptions({
    query = {},
  }: {
    query?: GetConsecutiveNumberFilterOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetConsecutiveNumberFilterOptionsQuery,
      ),
    ]);

    let requestPath = '/event/available_filters/consecutive_number_options';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      EventAvailableFiltersConsecutiveNumberOptionsResponse,
    );

    return response;
  }

  async getEmployeeFilterOptions({
    query = {},
  }: {
    query?: GetEmployeeFilterOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetEmployeeFilterOptionsQuery),
    ]);

    let requestPath = '/event/available_filters/employee_options';

    const result = await this.http.get(requestPath, query, { withSide: true });

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getAttrOptions({
    query = {},
    params,
  }: {
    query?: GetAttrOptionsQuery;
    params: GetAttrOptionsParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetAttrOptionsQuery),
      this.validator.validateOrReject(params, GetAttrOptionsParams),
    ]);

    let requestPath = '/event/available_filters/attr_options/{attr_code}';

    const paramsKeys = Object.keys(params) as (keyof GetAttrOptionsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, { withSide: true });

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getAvailableEventFiltersAbsence({
    query = {},
  }: {
    query?: GetAvailableEventFiltersAbsenceQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetAvailableEventFiltersAbsenceQuery,
      ),
    ]);

    let requestPath = '/event/absence_available_filters';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      AvailableFilters,
    );

    return response;
  }

  async getAvailableEventFilters({
    query = {},
  }: {
    query?: GetAvailableEventFiltersQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetAvailableEventFiltersQuery),
    ]);

    let requestPath = '/event/available_filters';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      FiltersResponse,
    );

    return response;
  }

  async getCreateEventOptionsEventTypes({
    query,
  }: {
    query: GetCreateEventOptionsEventTypesQuery;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetCreateEventOptionsEventTypesQuery,
      ),
    ]);

    let requestPath = '/event/create_options/event_types';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreateEventOptionsEventTypes,
    );

    return response;
  }

  async getEventTypeCreateOptions({
    query,
  }: {
    query: GetEventTypeCreateOptionsQuery;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetEventTypeCreateOptionsQuery),
    ]);

    let requestPath = '/event/create_options/event_type';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreateEventTypeOptions,
    );

    return response;
  }

  async employeeCompetencyEvents({
    params,
    body = {},
  }: {
    params: EmployeeCompetencyEventsParams;
    body?: EmployeeCompetencyEventsRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, EmployeeCompetencyEventsParams),
      this.validator.validateOrReject(body, EmployeeCompetencyEventsRequest),
    ]);

    let requestPath = '/employee/{employee_id}/competency/events';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof EmployeeCompetencyEventsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      EmployeeCompetencyEventsResponse,
    );

    return response;
  }

  async employeeCompetency({ params }: { params: EmployeeCompetencyParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, EmployeeCompetencyParams),
    ]);

    let requestPath = '/employee/{employee_id}/competency';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof EmployeeCompetencyParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      EmployeeCompetencyResponse,
    );

    return response;
  }

  async getCreateEventOptionsEmployeeEventTypes({
    query = {},
    params,
  }: {
    query?: GetCreateEventOptionsEmployeeEventTypesQuery;
    params: GetCreateEventOptionsEmployeeEventTypesParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetCreateEventOptionsEmployeeEventTypesQuery,
      ),
      this.validator.validateOrReject(
        params,
        GetCreateEventOptionsEmployeeEventTypesParams,
      ),
    ]);

    let requestPath =
      '/event/create_options/employees/{employee_id}/event_types';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCreateEventOptionsEmployeeEventTypesParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      EmployeeCreatableEventTypes,
    );

    return response;
  }

  async getCreateEventOptionsEmployees({
    query,
  }: {
    query: GetCreateEventOptionsEmployeesQuery;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetCreateEventOptionsEmployeesQuery,
      ),
    ]);

    let requestPath = '/event/create_options/employees';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(result, EmployeesList);

    return response;
  }

  async eventCreateOptionsEventTypeParticipantOptions({
    query,
  }: {
    query: EventCreateOptionsEventTypeParticipantOptionsQuery;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        EventCreateOptionsEventTypeParticipantOptionsQuery,
      ),
    ]);

    let requestPath = '/event/create_options/event_type/participant_options';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreateEventTypeParticipantOptions,
    );

    return response;
  }

  async getCreateEventOptionsCompanies({
    query = {},
  }: {
    query?: GetCreateEventOptionsCompaniesQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetCreateEventOptionsCompaniesQuery,
      ),
    ]);

    let requestPath = '/event/create_options/companies';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreateEventCompanyOptionsList,
    );

    return response;
  }

  async getCreateEventOptionsCommon({
    query,
  }: {
    query: GetCreateEventOptionsCommonQuery;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetCreateEventOptionsCommonQuery),
    ]);

    let requestPath = '/event/create_options/common';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreateEventCommonOptionsResponse,
    );

    return response;
  }

  async eventListExport({ body }: { body: EventListExportRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, EventListExportRequest),
    ]);

    let requestPath = '/event/list/export';

    const result = await this.http.generateFile(requestPath, body, {
      withSide: true,
    });

    return result;
  }

  async getEventsToPaper({
    body = {},
  }: {
    body?: EventListToPaperRequest;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(body, EventListToPaperRequest),
    ]);

    let requestPath = '/event/list/to_paper';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      EventListToPaperResponse,
    );

    return response;
  }

  async getDocumentCountByFilter({
    body = {},
  }: {
    body?: EventDocumentCountRequest;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(body, EventDocumentCountRequest),
    ]);

    let requestPath = '/event/document_count';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      EventDocumentCountResponse,
    );

    return response;
  }

  async getEventsPdf({
    body = {},
  }: {
    body?: EventPdfRequest;
  } = {}) {
    await Promise.all([this.validator.validateOrReject(body, EventPdfRequest)]);

    let requestPath = '/event/pdf';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      EventPdfResponse,
    );

    return response;
  }

  async getEventsXlsx({
    query,
    body = {},
  }: {
    query: GetEventsXlsxQuery;
    body?: EventXlsxRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetEventsXlsxQuery),
      this.validator.validateOrReject(body, EventXlsxRequest),
    ]);

    let requestPath = '/event/xlsx';

    requestPath = `${requestPath}${
      query && Object.keys(query).length
        ? `?${this.query.stringify(query)}`
        : ''
    }`;
    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      EventXlsxResponse,
    );

    return response;
  }

  async getEvents({
    body = {},
  }: {
    body?: EventListRequest;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(body, EventListRequest),
    ]);

    let requestPath = '/event/list';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      EventListResponse,
    );

    return response;
  }

  async candidateEventListAvailableFilters() {
    let requestPath = '/candidate/event/list/available_filters';

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      FiltersResponse,
    );

    return response;
  }

  async candidateEventList({
    body = {},
  }: {
    body?: CandidateEventListRequest;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(body, CandidateEventListRequest),
    ]);

    let requestPath = '/candidate/event/list';

    const result = await this.http.post(requestPath, body, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      CandidateEventListResponse,
    );

    return response;
  }

  async getEventBatchEmployeeCount({
    query,
  }: {
    query: GetEventBatchEmployeeCountQuery;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetEventBatchEmployeeCountQuery),
    ]);

    let requestPath = '/event/batch/employee_count';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      EventBatchEmployeeCountResponse,
    );

    return response;
  }

  async getEventBatchEmployees({
    query,
  }: {
    query: GetEventBatchEmployeesQuery;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetEventBatchEmployeesQuery),
    ]);

    let requestPath = '/event/batch/employees';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      EventBatchEmployeesResponse,
    );

    return response;
  }

  async createEventBatch({ body }: { body: CreateEventBatchRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, CreateEventBatchRequest),
    ]);

    let requestPath = '/event/batch';

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreateEventBatchResponse,
    );

    return response;
  }

  async getEventBatchList({ query }: { query: GetEventBatchListQuery }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetEventBatchListQuery),
    ]);

    let requestPath = '/event/batch';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      EventBatchListResponse,
    );

    return response;
  }

  async createEvent({ body }: { body: CreateEventRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, CreateEventRequest),
    ]);

    let requestPath = '/event';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreateEventResponse,
    );

    return response;
  }

  async getCancelReasons({
    query = {},
  }: {
    query?: GetCancelReasonsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetCancelReasonsQuery),
    ]);

    let requestPath = '/event/cancel_reasons';

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      CancelReasonListResponse,
    );

    return response;
  }
}
